<template>
    <!-- Wallet Card -->
    <div class="section pt-1">
        <div class="wallet-card">
            <!-- Balance -->
            <div class="balance">
                <div class="left">
                    <span class="title mal-text">{{ target_title }}</span>
                    <h1 class="total text-primary">₹{{ count_target.toFixed(2) }}</h1>
                </div>
                <div class="right">
                    <div id="chart">
                        <apexchart type="pie" width="400" :options="chartOptions" :series="series"></apexchart>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="section">
        <div class="row mt-1">
            <div class="col-6">
                <div class="stat-box">
                    <div class="title mal-text">കിട്ടിയത്</div>
                    <div class="value text-success">₹{{ count_total.toFixed(2) }}</div>
                </div>
            </div>
            <div class="col-6">
                <div class="stat-box">
                    <div class="title mal-text">ബാക്കിയുള്ളത്</div>
                    <div class="value text-danger">₹{{ minusToZero ? minusToZero : '00.0' }}</div>
                </div>
            </div>
            <!-- Zone / District / State report: total state / district / zone / unit share from this level -->
            <template v-if="user_level === 3 || user_level === 2 || user_level === 1">
                <div class="col-6 mt-1">
                    <div class="stat-box">
                        <div class="title mal-text">സ്റ്റേറ്റ് വിഹിതം</div>
                        <div class="value text-warning">₹{{ displayStateShare.toFixed(2) }}</div>
                    </div>
                </div>
                <div class="col-6 mt-1">
                    <div class="stat-box">
                        <div class="title mal-text">ശാഖാ വിഹിതം</div>
                        <div class="value text-info">₹{{ displayUnitShare.toFixed(2) }}</div>
                    </div>
                </div>
                <div class="col-6 mt-1">
                    <div class="stat-box">
                        <div class="title mal-text">മണ്ഡലം വിഹിതം</div>
                        <div class="value text-secondary">₹{{ displayZoneShare.toFixed(2) }}</div>
                    </div>
                </div>
                <div class="col-6 mt-1">
                    <div class="stat-box">
                        <div class="title mal-text">ജില്ല വിഹിതം</div>
                        <div class="value text-primary">₹{{ displayDistrictShare.toFixed(2) }}</div>
                    </div>
                </div>
            </template>
        </div>
    </div>

    <!-- Receipt book % + Report button — always visible for state/district/zone admins -->
    <div v-if="is_youth_dawa_fund && (user_level === 1 || user_level === 2 || user_level === 3)"
        style="display:flex;align-items:center;justify-content:space-between;padding:6px 16px;">
        <span style="font-size:12px;font-weight:600;color:#94a3b8;" class="mal-text">
            കരട് &amp; റസീപ്റ്റ് റിപ്പോർട്ട്
        </span>
        <div style="display:flex;align-items:center;gap:6px;">
            <button type="button" @click="goToKaradReport"
                style="display:inline-flex;align-items:center;gap:5px;padding:6px 14px;background:#1a418c;color:#fff;font-size:12px;font-weight:700;border-radius:8px;border:none;cursor:pointer;white-space:nowrap;">
                <ion-icon name="document-text-outline" style="font-size:14px;"></ion-icon>
                Report
            </button>
            <button v-if="user_level === 3" type="button" class="export-pdf-btn" @click="exportZoneAllocationPdf()" title="Export Receipt Book PDF">
                <ion-icon name="document-text-outline"></ion-icon>
                <span>PDF</span>
            </button>
            <!-- State: all-districts report. District: own district's zones report. -->
            <button v-if="user_level === 1 || user_level === 2" type="button" class="export-pdf-btn" @click="exportProgressReport()" :disabled="pdf_exporting" title="Download Progress Report">
                <span v-if="pdf_exporting" class="spinner-border spinner-border-sm"></span>
                <ion-icon v-else name="document-text-outline"></ion-icon>
                <span>{{ pdf_exporting ? 'Generating…' : 'Download Report' }}</span>
            </button>
        </div>
    </div>

    <!-- Karad list pledge stats (state / district / zone) -->
    <div class="section" v-if="is_youth_dawa_fund && (user_level === 1 || user_level === 2 || user_level === 3) && pledge_stats.total > 0">
        <div class="row mt-1" style="cursor:pointer;" @click="openPledgeBreakdownModal()">
            <div class="col-12 mb-1">
                <div style="display:flex;align-items:center;justify-content:space-between;padding:0 4px;">
                    <span style="font-size:12px;font-weight:700;color:#888;letter-spacing:0.5px;" class="mal-text">
                        കരട് ലിസ്റ്റ്
                        <ion-icon name="chevron-down-outline" style="font-size:12px;vertical-align:middle;margin-left:3px;"></ion-icon>
                    </span>
                </div>
            </div>
            <div class="col-4">
                <div class="stat-box" style="text-align:center;">
                    <div class="title mal-text">ചേർത്തത്</div>
                    <div class="value" style="color:#1a418c;">{{ pledge_stats.total }}</div>
                </div>
            </div>
            <div class="col-4">
                <div class="stat-box" style="text-align:center;">
                    <div class="title mal-text">കാണേണ്ടത്</div>
                    <div class="value text-danger">{{ pledge_stats.members_count }}</div>
                </div>
            </div>
            <div class="col-4">
                <div class="stat-box" style="text-align:center;">
                    <div class="title mal-text">കണ്ടത്</div>
                    <div class="value text-success">{{ pledge_stats.collected }}</div>
                </div>
            </div>
        </div>
    </div>

    <!-- Pledge Breakdown Modal -->
    <div class="modal fade action-sheet" id="pledgeBreakdownModel" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title mal-text">കരട് ലിസ്റ്റ് — {{ pledgeBreakdownTitle }}</h5>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content">
                        <div v-if="pledgeBreakdownLoading" class="text-center py-3">
                            <div class="spinner-border spinner-border-sm text-secondary"></div>
                        </div>
                        <div v-else-if="!pledgeBreakdownRows.length" class="text-center text-muted py-3 mal-text">
                            ഡേറ്റ ഇല്ല
                        </div>
                        <table v-else class="table table-sm" style="font-size:13px;">
                            <thead>
                                <tr>
                                    <th class="mal-text">പേര്</th>
                                    <th class="text-center mal-text">ചേർത്തത്</th>
                                    <th class="text-center mal-text">കാണേണ്ടത്</th>
                                    <th class="text-center mal-text">കണ്ടത്</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in pledgeBreakdownRows" :key="row.name">
                                    <td style="font-weight:600;">{{ row.name }}</td>
                                    <td class="text-center" style="color:#1a418c;font-weight:700;">{{ row.total }}</td>
                                    <td class="text-center text-danger font-weight-bold">{{ row.members_count }}</td>
                                    <td class="text-center text-success font-weight-bold">{{ row.collected }}</td>
                                </tr>
                            </tbody>
                            <tfoot style="border-top:2px solid #ddd;">
                                <tr>
                                    <td style="font-weight:700;" class="mal-text">ആകെ</td>
                                    <td class="text-center" style="color:#1a418c;font-weight:700;">{{ pledgeBreakdownRows.reduce((s,r)=>s+r.total,0) }}</td>
                                    <td class="text-center text-danger" style="font-weight:700;">{{ pledgeBreakdownRows.reduce((s,r)=>s+(r.members_count||0),0) }}</td>
                                    <td class="text-center text-success" style="font-weight:700;">{{ pledgeBreakdownRows.reduce((s,r)=>s+r.collected,0) }}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>


    <!-- Own receipt books section for district (level 2) and zone (level 3) -->
    <div class="section mt-2 mb-1" v-if="is_youth_dawa_fund && (user_level === 2 || user_level === 3) && thisLevelBooks.length">
        <!-- All received — green -->
        <span v-if="hasReceivedOwnBook && pendingOwnBooksCount === 0"
            style="display:flex;align-items:center;justify-content:space-between;width:100%;padding:10px 14px;background:#f0fff6;border:2px solid #1DCC70;border-radius:10px;pointer-events:none;box-shadow:0 2px 6px rgba(29,204,112,0.12);">
            <div style="display:flex;align-items:center;gap:6px;">
                <ion-icon name="checkmark-circle-outline" style="font-size:18px;color:#1DCC70;"></ion-icon>
                <span style="font-size:12px;font-weight:600;color:#1DCC70;" class="mal-text">ബുക്ക് ലഭിച്ചു</span>
            </div>
            <span style="font-size:22px;font-weight:900;color:#1DCC70;">100%</span>
        </span>
        <!-- Partial — orange, tap to confirm remaining -->
        <button v-else-if="hasReceivedOwnBook && pendingOwnBooksCount > 0" type="button"
            style="display:flex;align-items:center;justify-content:space-between;width:100%;padding:10px 14px;background:#fff8ed;border:2px solid #f0a500;border-radius:10px;cursor:pointer;box-shadow:0 2px 6px rgba(240,165,0,0.12);"
            @click="markAllOwnBooksReceivedConfirmation()">
            <div style="display:flex;align-items:center;gap:6px;">
                <ion-icon name="alert-circle-outline" style="font-size:18px;color:#f0a500;"></ion-icon>
                <div style="text-align:left;">
                    <div style="font-size:12px;font-weight:700;color:#f0a500;" class="mal-text">{{ pendingOwnBooksCount }} ബുക്ക് ലഭ്യമായിട്ടില്ല</div>
                    <div style="font-size:10px;color:#aaa;" class="mal-text">ടാപ്പ് ചെയ്ത് confirm ചെയ്യുക</div>
                </div>
            </div>
            <span style="font-size:22px;font-weight:900;color:#f0a500;">{{ Math.round((thisLevelBooks.length - pendingOwnBooksCount) / thisLevelBooks.length * 100) }}%</span>
        </button>
        <!-- Nothing received yet — red -->
        <button v-else type="button"
            style="display:flex;align-items:center;justify-content:space-between;width:100%;padding:10px 14px;background:#fff0f0;border:2px solid #e74c3c;border-radius:10px;cursor:pointer;box-shadow:0 2px 6px rgba(231,76,60,0.12);"
            @click="markAllOwnBooksReceivedConfirmation()">
            <div style="display:flex;align-items:center;gap:6px;">
                <ion-icon name="book-outline" style="font-size:18px;color:#e74c3c;"></ion-icon>
                <div style="text-align:left;">
                    <div style="font-size:12px;font-weight:700;color:#e74c3c;" class="mal-text">ബുക്ക് ലഭിച്ചിട്ടില്ല</div>
                    <div style="font-size:10px;color:#aaa;" class="mal-text">ടാപ്പ് ചെയ്ത് confirm ചെയ്യുക</div>
                </div>
            </div>
            <span style="font-size:22px;font-weight:900;color:#e74c3c;">0%</span>
        </button>
    </div>

    <!-- Zone level: export PDF button -->

    <!-- Help Desk (district / zone admins) — non Youth Dawa Fund -->
    <div class="section mt-1 mb-2" v-if="(user_level === 2 || user_level === 3) && !is_youth_dawa_fund">
        <help-desk-card />
    </div>

    <!-- Youth Dawa Fund: Help Desk button (left) + Tutorials (right).
         Hidden on the state dashboard (sub_level 1) — tutorials are per district/zone/unit. -->
    <div class="section mt-1 mb-2" v-if="is_youth_dawa_fund && Number($route.params.sub_level) !== 1"
        style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
        <help-desk-card as-button v-if="user_level === 2 || user_level === 3" />
        <span v-else></span>
        <tutorial-button category="Youth Dawa Fund" id="ydfTutorialSheet"
            :level="Number($route.params.sub_level)" />
    </div>

    <div class="section pt-0 mt-0">
        <div class="listed-detail mt-3">
            <h3 class="text-center mt-2 mal-text custom-title">{{ page_title }}</h3>
        </div>
    </div>

    <div class="section text-center" v-if="loading_list">
        <div class="spinner-border text-secondary" role="status"></div>
    </div>

    <div class="listed-detail mt-0">
        <h4 class="text-center mt-0 mal-text" v-if="!loading_list && member_lists.total <= 0">ഇതുവരെ ആരെയും
            ചേർത്തിട്ടില്ല</h4>
    </div>
    <div class="modal fade action-sheet" id="nerpathamTargetModel" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Nerpatham Target</h5>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content">
                        <Form @submit="addNerpathamTarget">
                            <div class="form-group basic">
                                <label class="label" for="Target">Target</label>
                                <div class="form-group boxed">
                                    <Field name="target" type="number" class="form-control" id="target"
                                        v-model="nerpatham_target" placeholder="Enter Target" />
                                </div>
                                <input type="hidden" v-model="sub_level_id">
                                <div class="form-group" v-if="message">
                                    <div v-if="message" class="error-feedback">
                                        {{ message }}
                                    </div>
                                </div>
                            </div>
                            <div class="form-group basic">
                                <button type="submit" class="btn btn-primary btn-block btn-lg" :disabled="loading">
                                    <span v-show="loading" class="spinner-border spinner-border-sm"></span>
                                    <span>Save</span></button>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- Receipt Book Number Modal -->
    <div class="modal fade action-sheet" id="receiptBookModel" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title mal-text">റെസിപ്റ്റ് ബുക്ക് നമ്പർ</h5>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content">
                        <div class="form-group basic">
                            <label class="label">{{ receipt_book_unit_name }}</label>
                            <div class="form-group boxed">
                                <input type="text" class="form-control" v-model="receipt_book_no_input"
                                    placeholder="Receipt Book Number" />
                            </div>
                        </div>
                        <div class="form-group" v-if="message">
                            <div class="error-feedback">{{ message }}</div>
                        </div>
                        <div class="form-group basic">
                            <button type="button" class="btn btn-primary btn-block btn-lg"
                                :disabled="loading" @click="saveReceiptBookNo">
                                <span v-show="loading" class="spinner-border spinner-border-sm"></span>
                                <span>Save</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- View Books Modal (State admin: list books per district with edit/delete) -->
    <div class="modal fade action-sheet" id="viewBooksModel" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Receipt Books — {{ viewBooks_sub_name }}</h5>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content">
                        <div v-if="viewBooksLoading" class="text-center py-3">
                            <div class="spinner-border spinner-border-sm text-secondary"></div>
                        </div>
                        <div v-else-if="!viewBooksList.length" class="text-center text-muted py-3">
                            No books allocated yet.
                        </div>
                        <table v-else class="table table-sm">
                            <thead>
                                <tr>
                                    <th>Book #</th>
                                    <th>Receipt Range</th>
                                    <th class="mal-text">ഏൽപ്പിച്ചത്</th>
                                    <th>Status</th>
                                    <th>Amount</th>
                                    <th class="text-center">Visits</th>
                                    <th class="text-center">Receipts</th>
                                    <th class="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(book, idx) in viewBooksList" :key="book.id">
                                <tr v-if="idx > 0 && book.received && !viewBooksList[idx-1].received">
                                    <td colspan="7" style="background:#f0f0f0;text-align:center;font-size:11px;color:#888;padding:4px;letter-spacing:0.5px;">— Received —</td>
                                </tr>
                                <tr>
                                    <td>{{ book.book_number }}</td>
                                    <td>
                                        <span v-if="book.receipt_from && book.receipt_to">
                                            {{ book.receipt_from }}–{{ book.receipt_to }}
                                        </span>
                                        <span v-else class="text-muted">—</span>
                                    </td>
                                    <td style="font-size:12px;">
                                        <span v-if="book.holder_name" style="color:#27173E;">{{ book.holder_name }}</span>
                                        <span v-else class="text-muted">—</span>
                                    </td>
                                    <td>
                                        <span class="badge"
                                            :class="book.received
                                                ? (book.unit_id ? 'bg-success' : (book.zone_id ? 'bg-primary' : 'bg-info text-dark'))
                                                : (book.unit_id ? 'bg-warning text-dark' : (book.zone_id ? 'bg-warning text-dark' : 'bg-secondary'))">
                                            {{ book.received
                                                ? (book.unit_id ? 'Unit Received' : (book.zone_id ? 'Zone Received' : 'District Received'))
                                                : (book.unit_id ? 'With Unit' : (book.zone_id ? 'With Zone' : 'With District')) }}
                                        </span>
                                    </td>
                                    <td class="text-success text-nowrap">
                                        <span v-if="book.collected_amount > 0">₹{{ Number(book.collected_amount).toFixed(2) }}</span>
                                        <span v-else class="text-muted">—</span>
                                    </td>
                                    <td class="text-center text-nowrap">
                                        <span v-if="book.visit_count > 0" style="color:#1a418c;font-weight:600;">{{ book.visit_count }}</span>
                                        <span v-else class="text-muted">—</span>
                                    </td>
                                    <td class="text-center text-nowrap">
                                        <span v-if="book.receipt_count > 0" style="color:#1DCC70;font-weight:600;">{{ book.receipt_count }}</span>
                                        <span v-else class="text-muted">—</span>
                                    </td>
                                    <td style="white-space:nowrap;padding:4px 6px;">
                                        <span style="display:inline-flex;gap:4px;align-items:center;">
                                        <template v-if="user_level <= 3 && user_level === 2 && book.zone_id">
                                            <button @click="openEditBookModal(book)" title="Edit" style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;background:#e8eeff;border:none;border-radius:6px;color:#1a418c;cursor:pointer;padding:0;"><ion-icon name="pencil-outline" style="font-size:15px;"></ion-icon></button>
                                            <button @click="deleteBookConfirmation(book)" title="Recall" style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;background:#fee;border:none;border-radius:6px;color:#e74c3c;cursor:pointer;padding:0;"><ion-icon name="trash-outline" style="font-size:15px;"></ion-icon></button>
                                        </template>
                                        <template v-else-if="user_level <= 3 && user_level === 3 && book.unit_id">
                                            <button @click="openEditBookModal(book)" title="Edit" style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;background:#e8eeff;border:none;border-radius:6px;color:#1a418c;cursor:pointer;padding:0;"><ion-icon name="pencil-outline" style="font-size:15px;"></ion-icon></button>
                                            <button @click="deleteBookConfirmation(book)" title="Recall" style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;background:#fee;border:none;border-radius:6px;color:#e74c3c;cursor:pointer;padding:0;"><ion-icon name="trash-outline" style="font-size:15px;"></ion-icon></button>
                                        </template>
                                        <template v-else-if="user_level <= 3 && !book.received">
                                            <button @click="openEditBookModal(book)" title="Edit" style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;background:#e8eeff;border:none;border-radius:6px;color:#1a418c;cursor:pointer;padding:0;"><ion-icon name="pencil-outline" style="font-size:15px;"></ion-icon></button>
                                            <button @click="markBookReceived(book)" title="Mark Received" style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;background:#e8fff0;border:none;border-radius:6px;color:#1DCC70;cursor:pointer;padding:0;"><ion-icon name="checkmark-circle-outline" style="font-size:15px;"></ion-icon></button>
                                            <button @click="deleteBookConfirmation(book)" title="Delete" style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;background:#fee;border:none;border-radius:6px;color:#e74c3c;cursor:pointer;padding:0;"><ion-icon name="trash-outline" style="font-size:15px;"></ion-icon></button>
                                        </template>
                                        <button @click="openBookTimeline(book)" title="Timeline" style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;background:#f0f0ff;border:none;border-radius:6px;color:#6366f1;cursor:pointer;padding:0;"><ion-icon name="time-outline" style="font-size:15px;"></ion-icon></button>
                                        </span>
                                    </td>
                                </tr>
                                </template>
                            </tbody>
                        </table>

                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Receipt Book Timeline Modal -->
    <div class="modal fade action-sheet" id="bookTimelineModal" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title mal-text">
                        റസീപ്റ്റ് ബുക്ക് ടൈംലൈൻ
                        <span v-if="timeline_book" style="color:#1a418c;"> — Book #{{ timeline_book.book_number }}</span>
                    </h5>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content" style="max-height:480px;overflow-y:auto;">
                        <!-- Book info bar -->
                        <div v-if="timeline_book && timeline_book.receipt_from" style="background:#f4f7ff;border-radius:8px;padding:10px 14px;margin-bottom:14px;">
                            <div style="display:flex;gap:16px;flex-wrap:wrap;margin-bottom:6px;">
                                <div>
                                    <div style="font-size:10px;color:#64748b;font-weight:500;" class="mal-text">ലഭ്യമായ ലീഫ് നമ്പർ</div>
                                    <div style="font-size:13px;font-weight:700;color:#1a418c;">
                                        {{ timeline_book.receipt_from }} – {{ timeline_book.receipt_to }}
                                    </div>
                                </div>
                                <div>
                                    <div style="font-size:10px;color:#64748b;font-weight:500;" class="mal-text">ആകെ ലീഫ്</div>
                                    <div style="font-size:13px;font-weight:700;color:#374151;">
                                        {{ timeline_book.receipt_to - timeline_book.receipt_from + 1 }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="timeline_loading" class="text-center py-4">
                            <div class="spinner-border spinner-border-sm text-secondary"></div>
                        </div>
                        <div v-else-if="!timeline_entries.length && !(timeline_book && timeline_book.holder_name)" class="text-center py-4">
                            <ion-icon name="time-outline" style="font-size:32px;color:#cbd5e1;"></ion-icon>
                            <p class="mal-text mt-2" style="font-size:13px;color:#94a3b8;">ഇതുവരെ ഒരു ചുമതലക്കാരനും accept ചെയ്തിട്ടില്ല</p>
                        </div>
                        <div v-else-if="!timeline_entries.length && timeline_book && timeline_book.holder_name" style="display:flex;gap:12px;position:relative;">
                            <div style="width:40px;height:40px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:#1a418c;color:#fff;font-size:18px;box-shadow:0 0 0 2px #1a418c;">
                                <ion-icon name="person"></ion-icon>
                            </div>
                            <div style="flex:1;background:#fff;border:1px solid #e2e8f0;border-radius:10px;padding:10px 12px;">
                                <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
                                    <span style="font-size:13px;font-weight:700;color:#1e293b;">{{ timeline_book.holder_name }}</span>
                                    <span style="font-size:10px;font-weight:600;background:#dcfce7;color:#16a34a;padding:2px 7px;border-radius:20px;">✓ നിലവിൽ</span>
                                </div>
                                <div v-if="timeline_book.received_at" style="font-size:11px;color:#64748b;">
                                    <span class="mal-text" style="color:#94a3b8;">Accept: </span>
                                    <strong style="color:#374151;">{{ timeline_book.received_at }}</strong>
                                </div>
                            </div>
                        </div>
                        <div v-else-if="timeline_entries.length" style="position:relative;">
                            <!-- Vertical line -->
                            <div style="position:absolute;left:20px;top:0;bottom:0;width:2px;background:#e2e8f0;"></div>

                            <div v-for="entry in timeline_entries" :key="entry.id"
                                style="display:flex;gap:12px;margin-bottom:20px;position:relative;">
                                <!-- Circle -->
                                <div :style="{
                                    width: '40px', height: '40px', borderRadius: '50%', flexShrink: '0',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: entry.is_current ? '#1a418c' : '#e2e8f0',
                                    color: entry.is_current ? '#fff' : '#64748b',
                                    fontSize: '18px', zIndex: '1', border: '3px solid #fff',
                                    boxShadow: '0 0 0 2px ' + (entry.is_current ? '#1a418c' : '#cbd5e1')
                                }">
                                    <ion-icon :name="entry.is_current ? 'person' : 'person-outline'"></ion-icon>
                                </div>

                                <!-- Content card -->
                                <div style="flex:1;min-width:0;background:#fff;border:1px solid #e2e8f0;border-radius:10px;padding:10px 12px;">
                                    <!-- Header row -->
                                    <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-bottom:4px;">
                                        <span style="font-size:13px;font-weight:700;color:#1e293b;">{{ entry.holder_name || '—' }}</span>
                                        <span v-if="entry.is_current"
                                            style="font-size:10px;font-weight:600;background:#dcfce7;color:#16a34a;padding:2px 7px;border-radius:20px;">
                                            ✓ നിലവിൽ
                                        </span>
                                    </div>
                                    <!-- Dates -->
                                    <div style="font-size:11px;color:#64748b;margin-bottom:6px;">
                                        <span class="mal-text" style="color:#94a3b8;">Accept: </span>
                                        <strong style="color:#374151;">{{ entry.started_at }}</strong>
                                        <span v-if="entry.ended_at">
                                            <br><span class="mal-text" style="color:#94a3b8;">മാറ്റിയത്: </span>
                                            <strong style="color:#374151;">{{ entry.ended_at }}</strong>
                                        </span>
                                        <span v-else>
                                            &nbsp;<span style="font-size:10px;color:#16a34a;" class="mal-text">— നിലവിൽ</span>
                                        </span>
                                    </div>
                                    <!-- Stats row -->
                                    <div style="display:flex;gap:10px;flex-wrap:wrap;">
                                        <div style="background:#f0fdf4;border-radius:6px;padding:4px 10px;text-align:center;">
                                            <div style="font-size:16px;font-weight:800;color:#16a34a;">{{ entry.collections_count }}</div>
                                            <div style="font-size:10px;color:#64748b;" class="mal-text">ശേഖരിച്ചത്</div>
                                        </div>
                                        <div style="background:#f0f9ff;border-radius:6px;padding:4px 10px;text-align:center;">
                                            <div style="font-size:16px;font-weight:800;color:#0369a1;">₹{{ Number(entry.collections_amount).toFixed(0) }}</div>
                                            <div style="font-size:10px;color:#64748b;" class="mal-text">തുക</div>
                                        </div>
                                        <div v-if="timeline_book && timeline_book.receipt_from" style="background:#fef9c3;border-radius:6px;padding:4px 10px;text-align:center;">
                                            <div style="font-size:16px;font-weight:800;color:#92400e;">
                                                {{ timeline_book.receipt_to - timeline_book.receipt_from + 1 - entry.collections_count > 0
                                                    ? timeline_book.receipt_to - timeline_book.receipt_from + 1 - entry.collections_count
                                                    : 0 }}
                                            </div>
                                            <div style="font-size:10px;color:#64748b;" class="mal-text">ബാക്കി</div>
                                        </div>
                                    </div>
                                    <!-- Receipt numbers used -->
                                    <div v-if="entry.receipt_numbers && entry.receipt_numbers.length" style="margin-top:8px;">
                                        <div style="font-size:10px;color:#94a3b8;margin-bottom:4px;" class="mal-text">ഉപയോഗിച്ച ലീഫ് നമ്പറുകൾ:</div>
                                        <div style="display:flex;flex-wrap:wrap;gap:4px;">
                                            <span v-for="n in entry.receipt_numbers" :key="n"
                                                style="font-size:11px;background:#e0e7ff;color:#3730a3;padding:2px 7px;border-radius:4px;font-weight:600;">
                                                {{ n }}
                                            </span>
                                        </div>
                                    </div>
                                    <!-- Changed by -->
                                    <div v-if="entry.changed_by_name" style="margin-top:6px;font-size:11px;color:#94a3b8;">
                                        <ion-icon name="swap-horizontal-outline" style="vertical-align:middle;margin-right:2px;"></ion-icon>
                                        <span class="mal-text">ചുമതല നൽകിയത്: </span> {{ entry.changed_by_name }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Edit Book Modal -->
    <div class="modal fade action-sheet" id="editBookModel" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Edit Receipt Book</h5>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content">
                        <!-- All levels: book number only -->
                        <div class="form-group basic" v-if="user_level >= 2 && user_level <= 3">
                            <label class="label">Book Number</label>
                            <div style="max-height:220px; overflow-y:auto; border:1px solid #eee; border-radius:8px; padding:4px 0;">
                                <label v-for="book in availableBooks" :key="book"
                                    class="d-flex align-items-center gap-2 px-3 py-2"
                                    style="cursor:pointer; border-bottom:1px solid #f2f2f2; margin:0;">
                                    <input type="radio" :value="book" v-model="edit_book_number"
                                        style="width:18px;height:18px;accent-color:#4f46e5;flex-shrink:0;">
                                    <span style="font-size:15px;">Book #{{ book }}</span>
                                </label>
                            </div>
                        </div>
                        <div class="form-group basic" v-if="user_level === 1">
                            <label class="label">Book Number</label>
                            <div class="form-group boxed">
                                <input type="number" class="form-control" v-model="edit_book_number" />
                            </div>
                        </div>
                        <!-- Holder / entrusted member (zone→unit only) -->
                        <div class="form-group basic" v-if="user_level === 3" style="margin-top:8px;">
                            <label class="label mal-text">ആർക്ക് ഏൽപ്പിച്ചിരിക്കുന്നു</label>
                            <div v-if="edit_holder_selected"
                                style="display:flex;align-items:center;gap:8px;background:#eef3ff;border-radius:8px;padding:8px 12px;margin-bottom:6px;">
                                <div style="flex:1;">
                                    <div style="font-size:13px;font-weight:700;color:#1a418c;">{{ edit_holder_selected.name }}</div>
                                    <div style="font-size:11px;color:#888;">{{ edit_holder_selected.mobile }}</div>
                                </div>
                                <button @click="edit_holder_selected = null; edit_holder_search = ''"
                                    style="background:none;border:none;color:#aaa;font-size:18px;cursor:pointer;padding:0;">
                                    <ion-icon name="close-circle-outline"></ion-icon>
                                </button>
                            </div>
                            <div v-else style="position:relative;">
                                <div style="display:flex;gap:6px;align-items:center;">
                                    <input v-model="edit_holder_search" @input="searchEditHolder"
                                        class="form-control form-control-sm"
                                        placeholder="പേര് / മൊബൈൽ തിരയൂ"
                                        style="flex:1;" />
                                    <div v-if="edit_holder_searching" style="padding:0 8px;">
                                        <div class="spinner-border spinner-border-sm text-secondary"></div>
                                    </div>
                                </div>
                                <div v-if="edit_holder_results.length"
                                    style="border:1px solid #ddd;border-radius:8px;margin-top:4px;max-height:150px;overflow-y:auto;background:#fff;position:absolute;width:100%;z-index:999;">
                                    <div v-for="m in edit_holder_results" :key="m.id"
                                        @click="edit_holder_selected = m; edit_holder_search = m.name; edit_holder_results = []"
                                        style="padding:8px 12px;border-bottom:1px solid #f0f0f0;cursor:pointer;">
                                        <div style="font-size:13px;font-weight:600;color:#27173E;">{{ m.name }}</div>
                                        <div style="font-size:11px;color:#888;">{{ m.mobile }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="form-group" v-if="message">
                            <div class="error-feedback">{{ message }}</div>
                        </div>
                        <div class="form-group basic">
                            <button type="button" class="btn btn-primary btn-block btn-lg"
                                :disabled="loading" @click="submitEditBook">
                                <span v-show="loading" class="spinner-border spinner-border-sm"></span>
                                <span>Update</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Receipt Book Allocation Modal (adaptive per level) -->
    <div class="modal fade action-sheet" id="allocateBookModel" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">{{ allocateTitle }}</h5>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content">
                        <div class="form-group basic">
                            <label class="label">{{ allocate_sub_name }}</label>
                        </div>
                        <!-- State→District: type book number range -->
                        <template v-if="user_level === 1">
                            <div class="form-group basic">
                                <label class="label">Book Number From</label>
                                <div class="form-group boxed">
                                    <input type="number" class="form-control" v-model="alloc_book_from" placeholder="e.g. 1" min="1" />
                                </div>
                            </div>
                            <div class="form-group basic">
                                <label class="label">Book Number To</label>
                                <div class="form-group boxed">
                                    <input type="number" class="form-control" v-model="alloc_book_to" placeholder="e.g. 20" min="1" />
                                </div>
                            </div>
                        </template>
                        <!-- District→Zone: checkbox list of available books -->
                        <template v-else-if="user_level === 2">
                            <div class="form-group basic">
                                <label class="label">Book Numbers</label>
                                <div v-if="!availableBooks.length" class="text-muted" style="font-size:13px;">No available books to allocate.</div>
                                <div v-else style="max-height:220px; overflow-y:auto; border:1px solid #eee; border-radius:8px; padding:4px 0;">
                                    <label v-for="book in availableBooks" :key="book"
                                        class="d-flex align-items-center gap-2 px-3 py-2"
                                        style="cursor:pointer; border-bottom:1px solid #f2f2f2; margin:0;">
                                        <input type="checkbox" :value="book" v-model="alloc_book_numbers"
                                            style="width:18px;height:18px;accent-color:#4f46e5;flex-shrink:0;">
                                        <span style="font-size:15px;">Book #{{ book }}</span>
                                    </label>
                                </div>
                                <div v-if="availableBooks.length" class="d-flex justify-content-between mt-1 px-1">
                                    <small class="text-muted">{{ alloc_book_numbers.length }} selected</small>
                                    <small style="color:#4f46e5;cursor:pointer;"
                                        @click="alloc_book_numbers = alloc_book_numbers.length === availableBooks.length ? [] : [...availableBooks]">
                                        {{ alloc_book_numbers.length === availableBooks.length ? 'Deselect All' : 'Select All' }}
                                    </small>
                                </div>
                            </div>
                        </template>
                        <!-- Zone→Unit: checkbox list of available books + holder search -->
                        <template v-else-if="user_level === 3">
                            <div class="form-group basic">
                                <label class="label">Book Numbers</label>
                                <div v-if="!availableBooks.length" class="text-muted" style="font-size:13px;">No available books to allocate.</div>
                                <div v-else style="max-height:200px; overflow-y:auto; border:1px solid #eee; border-radius:8px; padding:4px 0;">
                                    <label v-for="book in availableBooks" :key="book"
                                        class="d-flex align-items-center gap-2 px-3 py-2"
                                        style="cursor:pointer; border-bottom:1px solid #f2f2f2; margin:0;">
                                        <input type="checkbox" :value="book" v-model="alloc_book_numbers"
                                            style="width:18px;height:18px;accent-color:#4f46e5;flex-shrink:0;">
                                        <span style="font-size:15px;">Book #{{ book }}</span>
                                    </label>
                                </div>
                                <div v-if="availableBooks.length" class="d-flex justify-content-between mt-1 px-1">
                                    <small class="text-muted">{{ alloc_book_numbers.length }} selected</small>
                                    <small style="color:#4f46e5;cursor:pointer;"
                                        @click="alloc_book_numbers = alloc_book_numbers.length === availableBooks.length ? [] : [...availableBooks]">
                                        {{ alloc_book_numbers.length === availableBooks.length ? 'Deselect All' : 'Select All' }}
                                    </small>
                                </div>
                            </div>
                            <!-- Holder / entrusted member -->
                            <div class="form-group basic" style="margin-top:4px;">
                                <label class="label mal-text" style="font-size:12px;color:#555;">ആർക്ക് ഏൽപ്പിക്കുന്നു</label>
                                <div v-if="alloc_holder_selected"
                                    style="display:flex;align-items:center;gap:8px;background:#eef3ff;border-radius:8px;padding:8px 12px;margin-bottom:6px;">
                                    <div style="flex:1;">
                                        <div style="font-size:13px;font-weight:700;color:#1a418c;">{{ alloc_holder_selected.name }}</div>
                                        <div style="font-size:11px;color:#888;">{{ alloc_holder_selected.mobile }}</div>
                                    </div>
                                    <button @click="alloc_holder_selected = null; alloc_holder_search = ''"
                                        style="background:none;border:none;color:#aaa;font-size:18px;cursor:pointer;padding:0;">
                                        <ion-icon name="close-circle-outline"></ion-icon>
                                    </button>
                                </div>
                                <div v-else style="position:relative;">
                                    <div style="display:flex;gap:6px;align-items:center;">
                                        <input v-model="alloc_holder_search" @input="searchAllocHolder"
                                            class="form-control form-control-sm"
                                            placeholder="പേര് / മൊബൈൽ തിരയൂ"
                                            style="flex:1;" />
                                        <div v-if="alloc_holder_searching" style="padding:0 8px;">
                                            <div class="spinner-border spinner-border-sm text-secondary"></div>
                                        </div>
                                    </div>
                                    <div v-if="alloc_holder_results.length"
                                        style="border:1px solid #ddd;border-radius:8px;margin-top:4px;max-height:150px;overflow-y:auto;background:#fff;position:absolute;width:100%;z-index:999;">
                                        <div v-for="m in alloc_holder_results" :key="m.id"
                                            @click="alloc_holder_selected = m; alloc_holder_search = m.name; alloc_holder_results = []"
                                            style="padding:8px 12px;border-bottom:1px solid #f0f0f0;cursor:pointer;">
                                            <div style="font-size:13px;font-weight:600;color:#27173E;">{{ m.name }}</div>
                                            <div style="font-size:11px;color:#888;">{{ m.mobile }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <div class="form-group" v-if="message">
                            <div class="error-feedback">{{ message }}</div>
                        </div>
                        <div class="form-group basic">
                            <button type="button" class="btn btn-primary btn-block btn-lg"
                                :disabled="loading" @click="submitAllocate">
                                <span v-show="loading" class="spinner-border spinner-border-sm"></span>
                                <span>Allocate</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="section mt-2 mb-2" v-if="!loading_list">
        <div style="display:flex;flex-direction:column;gap:8px;">
            <div
                v-for="(sub_level, idx) in member_lists" :key="sub_level.id"
                class="fc-card">

                <!-- Clickable main area -->
                <router-link
                    :to="user_level <= 2
                        ? '/org/' + (user_level + 1) + '/' + sub_level.id + '/fund-collection-report/' + $route.params.type
                        : '/org/' + (user_level + 1) + '/' + sub_level.id + '/fund-collection/' + $route.params.type"
                    class="fc-card-link">

                    <!-- Row 1: index + name -->
                    <div class="fc-row1">
                        <span class="fc-index">{{ idx + 1 }}</span>
                        <span class="fc-name"
                            :class="sub_level.amount == 0 ? 'text-danger' : (sub_level.target > 0 && sub_level.amount >= sub_level.target ? 'text-success' : 'text-primary')">
                            {{ sub_level.name }}
                        </span>
                        <ion-icon name="chevron-forward-outline" class="fc-chevron"></ion-icon>
                    </div>

                    <!-- Row 2: amount + right-side controls -->
                    <div class="fc-row2">
                        <!-- Left: amount + target label stacked -->
                        <div class="fc-amount-block">
                            <span class="fc-amount" :class="sub_level.amount > 0 ? 'text-success' : 'text-muted'">
                                ₹{{ sub_level.amount.toFixed(0) }}
                            </span>
                            <template v-if="(is_youth_dawa_fund && user_level <= 3 && sub_level.target > 0) || (!is_youth_dawa_fund && sub_level.target > 0)">
                                <span class="fc-target-label">ലക്ഷ്യം: ₹{{ sub_level.target.toFixed(0) }}</span>
                            </template>
                        </div>
                        <!-- Right: target button + books badge (fixed, no layout shift) -->
                        <div class="fc-row2-right">
                            <!-- Set target: only on units (view level 3), only for state/district admins.
                                 Once set, show the same green "added" badge as zone/district cards —
                                 still clickable here to edit the amount. -->
                            <a v-if="is_youth_dawa_fund && user_level === 3 && currentUser && currentUser.user_level <= 2 && sub_level.target > 0"
                                href="javascript:void(0)" @click.prevent="openTargetModal(sub_level)"
                                class="fc-target-added-badge">
                                <ion-icon name="flag"></ion-icon> ₹{{ sub_level.target.toFixed(0) }}
                            </a>
                            <a v-else-if="is_youth_dawa_fund && user_level === 3 && currentUser && currentUser.user_level <= 2"
                                href="javascript:void(0)" @click.prevent="openTargetModal(sub_level)"
                                class="fc-target-inline-btn">
                                <ion-icon name="flag-outline"></ion-icon>
                                <span>ടാർഗറ്റ്</span>
                            </a>
                            <!-- Zone card: show badge when unit targets have been set under it -->
                            <span v-else-if="is_youth_dawa_fund && user_level === 2 && sub_level.target > 0"
                                class="fc-target-added-badge">
                                <ion-icon name="flag"></ion-icon> ₹{{ sub_level.target.toFixed(0) }}
                            </span>
                            <template v-if="is_youth_dawa_fund">
                                <span v-if="receiptSummary[sub_level.id]" @click.prevent="openViewBooksModal(sub_level)"
                                    :class="receiptSummary[sub_level.id].received === receiptSummary[sub_level.id].total && receiptSummary[sub_level.id].total > 0 ? 'fc-books-done' : 'fc-books-pending'"
                                    class="fc-books-badge">
                                    <ion-icon name="book-outline"></ion-icon>
                                    {{ receiptSummary[sub_level.id].received }}/{{ receiptSummary[sub_level.id].total }}
                                </span>
                                <span v-else class="fc-books-badge fc-books-empty">
                                    <ion-icon name="book-outline"></ion-icon> 0/0
                                </span>
                            </template>
                        </div>
                    </div>

                    <!-- Progress bar -->
                    <div v-if="sub_level.target > 0 && (is_youth_dawa_fund ? user_level <= 2 : true)" class="fc-progress">
                        <div class="fc-progress-track">
                            <div class="fc-progress-fill" :style="{
                                width: Math.min(100, (sub_level.amount / sub_level.target) * 100).toFixed(1) + '%',
                                background: sub_level.amount >= sub_level.target ? '#1DCC70' : '#1a418c'
                            }"></div>
                        </div>
                    </div>
                </router-link>

                <!-- Books already given to this unit but with no incharge/holder set yet -->
                <div v-if="is_youth_dawa_fund && user_level === 3 && receiptSummary[sub_level.id]
                        && receiptSummary[sub_level.id].no_holder_book_numbers
                        && receiptSummary[sub_level.id].no_holder_book_numbers.length"
                    class="fc-no-holder-warning mal-text">
                    ബുക്ക് നമ്പർ {{ receiptSummary[sub_level.id].no_holder_book_numbers.join(', ') }} ന് ഇൻചാർജ് ഇല്ല
                </div>

                <!-- Action buttons — outside router-link so clicks don't navigate -->
                <div v-if="is_youth_dawa_fund && user_level <= 3" class="fc-actions">
                    <!-- Incharge + Book side by side (Target moved to amount row) -->
                    <a href="javascript:void(0)" @click.stop="openZoneInchargesModal(sub_level)"
                        class="fc-btn fc-btn-outline">
                        <ion-icon name="people-outline"></ion-icon>
                        <span>ഇൻചാർജ്</span>
                    </a>
                    <a href="javascript:void(0)" @click.stop="openAllocateModal(sub_level)"
                        class="fc-btn fc-btn-primary">
                        <ion-icon name="add-circle-outline"></ion-icon>
                        <span>ബുക്ക് ചേർക്കുക</span>
                    </a>
                    <!-- District-level card: officials + mandalam breakdown PDF -->
                    <a v-if="user_level === 1" href="javascript:void(0)" @click.stop="exportDistrictProgressPdf(sub_level)"
                        :style="pdf_exporting ? 'opacity:0.7;pointer-events:none;' : ''"
                        class="fc-btn fc-btn-outline fc-btn-row-break">
                        <span v-if="pdf_exporting" class="spinner-border spinner-border-sm"></span>
                        <ion-icon v-else name="document-text-outline"></ion-icon>
                        <span>{{ pdf_exporting ? 'Generating…' : 'Download Report' }}</span>
                    </a>
                </div>
            </div>
        </div>
    </div>
    <!-- <ul class="listview image-listview inset text" v-if="!loading_list && user_level != 4">
        <li v-for="sub_level in member_lists" v-bind:key="sub_level.id">
            <a class="item">
                <div class="in">
                    <router-link :to="'/org/' + (user_level + 1) + '/' + sub_level.id">
                        <div>
                            <b class="list-title">{{ sub_level.name }}</b>
                        </div>
                    </router-link>
                    <div class="card-button dropdown">
                        <button type="button" class="btn btn-link btn-icon" data-bs-toggle="modal"
                            :data-bs-target="'#actionSheetInset' + sub_level.id">
                            <ion-icon name="ellipsis-vertical-outline"></ion-icon>
                        </button>
                    </div>
                    <div class="modal fade action-sheet" :id="'actionSheetInset' + sub_level.id" tabindex="-1"
                        role="dialog">
                        <div class="modal-dialog" role="document">
                            <div class="modal-content">
                                <div class="modal-header">
                                    <h5 class="modal-title">Action</h5>
                                </div>
                                <div class="modal-body">
                                    <ul class="action-button-list">
                                        <li v-if="currentUser.user_level <= 3">
                                            <a href="javascript:void(0)" class="btn btn-list" data-bs-dismiss="modal"
                                                @click="nerpathamTarget(sub_level)">
                                                <span>
                                                    <ion-icon name="book-outline"></ion-icon>നേർപഥം ടാർഗറ്റ്
                                                </span>
                                            </a>
                                        </li>
                                        <li class="action-divider"></li>
                                        <li>
                                            <a href="#" class="btn btn-list text-danger" data-bs-dismiss="modal">
                                                <span>
                                                    <ion-icon name="close-outline"></ion-icon> Cancel
                                                </span>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </a>

        </li>
    </ul> -->
    <!-- Zone In-charges Modal (state admin, per district) -->
    <div class="modal fade action-sheet" id="zoneInchargesModel" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header" style="padding:12px 16px;">
                    <h5 class="modal-title mal-text" style="font-size:16px;font-weight:800;">ട്രഷറർ / ഇൻചാർജ്{{ zoneIncharges_district_name ? ' — ' + zoneIncharges_district_name : '' }}</h5>
                </div>
                <div class="modal-body" style="padding:6px 12px 14px;">
                    <div class="action-sheet-content">
                        <div v-if="zoneInchargesLoading" class="text-center py-3">
                            <div class="spinner-border spinner-border-sm text-secondary"></div>
                        </div>
                        <div v-else-if="!zoneInchargesList.length" class="text-center text-muted py-3 mal-text">
                            ഡേറ്റ ഇല്ല
                        </div>
                        <div v-else style="display:flex;flex-direction:column;gap:8px;">
                            <div v-for="ic in zoneInchargesList" :key="ic.exec_id || ic.id || ('lvl'+ic.user_level+'-d'+ic.district_id+'-z'+ic.zone_id+'-u'+ic.unit_id)"
                                style="background:#f8f9fd;border-radius:12px;padding:12px 14px;border:1px solid #eef0f5;">
                                <!-- Top row: badge + location -->
                                <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;flex-wrap:wrap;">
                                    <span style="font-size:11px;background:#e8eeff;color:#1a418c;border-radius:5px;padding:2px 9px;font-weight:800;flex-shrink:0;" class="mal-text">
                                        {{ ic.user_level === 2 ? 'ജില്ല' : ic.user_level === 3 ? 'മണ്ഡലം' : 'ശാഖ' }}
                                    </span>
                                    <span style="font-size:13px;color:#475569;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
                                        {{ ic.user_level === 3 ? ic.zone_name : ic.user_level === 4 ? ic.unit_name : ic.district_name }}
                                    </span>
                                    <span v-if="ic.is_override" style="font-size:10px;background:#fff3cd;color:#856404;border-radius:4px;padding:2px 6px;font-weight:700;">override</span>
                                </div>
                                <!-- Name -->
                                <div v-if="ic.name" style="font-size:17px;font-weight:800;color:#1e293b;line-height:1.25;margin-bottom:3px;">{{ ic.name }}</div>
                                <div v-else style="font-size:14px;font-weight:700;color:#aab;line-height:1.25;margin-bottom:3px;" class="mal-text">ഇതുവരെ ചേർത്തിട്ടില്ല</div>
                                <!-- Designation -->
                                <div v-if="ic.designation" style="font-size:12px;color:#94a3b8;margin-bottom:10px;" class="mal-text">{{ ic.designation }}</div>
                                <!-- Action buttons row -->
                                <div style="display:flex;align-items:center;gap:8px;margin-top:8px;">
                                    <button v-if="currentUser && (ic.user_level === 2 ? currentUser.user_level <= 2 : currentUser.user_level <= 3)" @click="openEditIncharge(ic)"
                                        style="display:inline-flex;align-items:center;gap:5px;padding:7px 14px;background:#eef0f5;border:none;border-radius:8px;color:#64748b;cursor:pointer;font-size:13px;font-weight:700;">
                                        <ion-icon :name="ic.name ? 'pencil-outline' : 'add-circle-outline'" style="font-size:15px;"></ion-icon>
                                        {{ ic.name ? 'Edit' : 'Add' }}
                                    </button>
                                    <template v-if="ic.mobile">
                                        <a :href="'tel:' + ic.mobile"
                                            style="display:inline-flex;align-items:center;gap:6px;padding:8px 16px;background:#1a418c;border-radius:8px;color:#fff;font-size:13px;font-weight:700;text-decoration:none;flex:1;justify-content:center;">
                                            <ion-icon name="call-outline" style="font-size:16px;"></ion-icon>
                                            Call
                                        </a>
                                        <a :href="'https://wa.me/91' + ic.mobile" target="_blank"
                                            style="display:inline-flex;align-items:center;gap:6px;padding:8px 16px;background:#25D366;border-radius:8px;color:#fff;font-size:13px;font-weight:700;text-decoration:none;flex:1;justify-content:center;">
                                            <ion-icon name="logo-whatsapp" style="font-size:16px;"></ion-icon>
                                            WhatsApp
                                        </a>
                                    </template>
                                    <span v-else style="font-size:12px;color:#ccc;" class="mal-text">നമ്പർ ഇല്ല</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Edit Incharge Modal -->
    <div class="modal fade action-sheet" id="editInchargeModel" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title mal-text">ഇൻചാർജ് മാറ്റുക</h5>
                    <a href="javascript:void(0)" data-bs-dismiss="modal" style="font-size:20px;color:#aaa;line-height:1;">
                        <ion-icon name="close-outline"></ion-icon>
                    </a>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content">
                        <div v-if="editIncharge_ic" style="margin-bottom:12px;background:#f8f8f8;border-radius:8px;padding:10px 12px;">
                            <div style="font-size:10px;color:#aaa;text-transform:uppercase;" class="mal-text">
                                {{ editIncharge_ic.user_level === 3 ? editIncharge_ic.zone_name : editIncharge_ic.user_level === 4 ? editIncharge_ic.unit_name : editIncharge_ic.district_name }}
                            </div>
                            <div style="font-size:13px;font-weight:700;color:#27173E;">{{ editIncharge_ic.name }}</div>
                        </div>
                        <div style="margin-bottom:10px;">
                            <label style="font-size:12px;color:#666;margin-bottom:4px;display:block;" class="mal-text">മെമ്പർ തിരയൂ</label>
                            <div style="display:flex;gap:6px;">
                                <input v-model="editIncharge_search" @input="searchInchargeMember"
                                    class="form-control form-control-sm" placeholder="പേര് / മൊബൈൽ"
                                    style="flex:1;" />
                                <div v-if="editIncharge_searching" style="display:flex;align-items:center;padding:0 8px;">
                                    <div class="spinner-border spinner-border-sm text-secondary"></div>
                                </div>
                            </div>
                            <div v-if="editIncharge_results.length" style="border:1px solid #ddd;border-radius:8px;margin-top:4px;max-height:180px;overflow-y:auto;background:#fff;">
                                <div v-for="m in editIncharge_results" :key="m.id" @click="selectInchargeMember(m)"
                                    style="padding:8px 12px;border-bottom:1px solid #f0f0f0;cursor:pointer;">
                                    <div style="font-size:13px;font-weight:600;color:#27173E;">{{ m.name }}</div>
                                    <div style="font-size:11px;color:#888;">{{ m.mobile }}</div>
                                </div>
                            </div>
                        </div>
                        <div v-if="editIncharge_selected" style="background:#eef3ff;border-radius:8px;padding:10px 12px;margin-bottom:12px;display:flex;align-items:center;justify-content:space-between;">
                            <div>
                                <div style="font-size:13px;font-weight:700;color:#1a418c;">{{ editIncharge_selected.name }}</div>
                                <div style="font-size:11px;color:#888;">{{ editIncharge_selected.mobile }}</div>
                            </div>
                            <button @click="editIncharge_selected = null" style="background:none;border:none;color:#aaa;font-size:18px;cursor:pointer;">
                                <ion-icon name="close-circle-outline"></ion-icon>
                            </button>
                        </div>
                        <div v-if="editIncharge_msg" style="font-size:12px;color:#e74c3c;margin-bottom:8px;" class="mal-text">{{ editIncharge_msg }}</div>
                        <button @click="saveInchargeOverride" :disabled="editIncharge_saving || !editIncharge_selected"
                            class="btn btn-primary btn-block mal-text" style="width:100%;">
                            <span v-if="editIncharge_saving"><div class="spinner-border spinner-border-sm"></div></span>
                            <span v-else>സേവ് ചെയ്യുക</span>
                        </button>
                        <button v-if="editIncharge_ic && editIncharge_ic.is_override"
                            @click="resetInchargeOverride"
                            :disabled="editIncharge_saving"
                            class="btn btn-block mal-text"
                            style="width:100%;margin-top:8px;background:#fff3f3;color:#e74c3c;border:1px solid #fdc5c5;font-weight:700;">
                            <ion-icon name="refresh-outline" style="vertical-align:middle;margin-right:4px;"></ion-icon>
                            <span v-if="editIncharge_resetting"><div class="spinner-border spinner-border-sm"></div></span>
                            <span v-else>ഡിഫോൾട്ടിലേക്ക് മടങ്ങുക</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Visitor Suggestions Modal -->
    <div class="modal fade action-sheet" id="visitorListModel" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title mal-text">ആരാണ് കാണേണ്ടത്</h5>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content">

                        <!-- Add form -->
                        <div style="background:#f8f8f8;border-radius:8px;padding:10px;margin-bottom:12px;">
                            <div class="form-group basic" v-if="currentUser && currentUser.user_level <= 1">
                                <div class="input-wrapper">
                                    <label class="label">Name</label>
                                    <input type="text" class="form-control" v-model="visitor_name" placeholder="Name" />
                                </div>
                            </div>
                            <div class="form-group basic" v-if="currentUser && currentUser.user_level <= 1">
                                <div class="input-wrapper">
                                    <label class="label">Mobile</label>
                                    <input type="text" class="form-control" v-model="visitor_mobile" placeholder="Mobile" />
                                </div>
                            </div>
                            <div class="form-group basic">
                                <div class="input-wrapper">
                                    <label class="label mal-text">Remarks</label>
                                    <textarea class="form-control" v-model="visitor_remarks" rows="2" placeholder="Remarks"></textarea>
                                </div>
                            </div>
                            <div v-if="visitorMsg" class="error-feedback mb-1">{{ visitorMsg }}</div>
                            <button type="button" class="btn btn-primary btn-block btn-sm"
                                :disabled="visitorSaving" @click="saveVisitorSuggestion">
                                <span v-show="visitorSaving" class="spinner-border spinner-border-sm"></span>
                                <span>Add</span>
                            </button>
                        </div>

                        <!-- List -->
                        <div v-if="visitorLoading" class="text-center py-2">
                            <div class="spinner-border spinner-border-sm text-secondary"></div>
                        </div>
                        <div v-else-if="!visitorList.length" class="text-center text-muted py-2 mal-text" style="font-size:13px;">
                            ആരെയും ചേർത്തിട്ടില്ല
                        </div>
                        <div v-else style="display:flex;flex-direction:column;gap:6px;">
                            <div v-for="v in visitorList" :key="v.id"
                                :style="{
                                    background: v.is_active ? '#fff' : '#f5f5f5',
                                    opacity: v.is_active ? 1 : 0.6,
                                    borderRadius: '8px', padding: '9px 12px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                                    display: 'flex', alignItems: 'center', gap: '8px'
                                }">
                                <div style="flex:1;min-width:0;">
                                    <div style="font-size:13px;font-weight:700;color:#27173E;display:flex;align-items:center;gap:5px;">
                                        {{ v.name }}
                                        <span :style="{fontSize:'10px',fontWeight:'600',padding:'1px 6px',borderRadius:'10px',background:v.is_active?'#1DCC70':'#aaa',color:'#fff'}">
                                            {{ v.is_active ? 'Active' : 'Inactive' }}
                                        </span>
                                    </div>
                                    <div v-if="v.remarks" style="font-size:11px;color:#888;margin-top:2px;">{{ v.remarks }}</div>
                                </div>
                                <template v-if="v.mobile && v.is_active">
                                    <a :href="'tel:' + v.mobile"
                                        style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;background:#1a418c;border-radius:50%;color:#fff;flex-shrink:0;">
                                        <ion-icon name="call-outline" style="font-size:16px;"></ion-icon>
                                    </a>
                                    <a :href="'https://wa.me/91' + v.mobile" target="_blank"
                                        style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;background:#25D366;border-radius:50%;color:#fff;flex-shrink:0;">
                                        <ion-icon name="logo-whatsapp" style="font-size:16px;"></ion-icon>
                                    </a>
                                </template>
                                <template v-if="currentUser && currentUser.user_level <= 2">
                                    <a href="javascript:void(0)" @click.prevent="toggleVisitorSuggestion(v)"
                                        :style="{display:'flex',alignItems:'center',justifyContent:'center',width:'32px',height:'32px',background:v.is_active?'#f0a500':'#1DCC70',borderRadius:'50%',color:'#fff',flexShrink:0}">
                                        <ion-icon :name="v.is_active ? 'pause-outline' : 'play-outline'" style="font-size:15px;"></ion-icon>
                                    </a>
                                    <a href="javascript:void(0)" @click.prevent="deleteVisitorSuggestion(v)"
                                        style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;background:#e74c3c;border-radius:50%;color:#fff;flex-shrink:0;">
                                        <ion-icon name="trash-outline" style="font-size:15px;"></ion-icon>
                                    </a>
                                </template>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    </div>

    <alert-model :type="alert_type" :message="alert_message" ref="alert_model"></alert-model>
    <dialog-model :message="dialog_message" :action_method="dialog_action" :action_method_params="dialog_action_params"
        ref="dialog_model"></dialog-model>

    <!-- Set Target Modal (standard action-sheet, same as Allocate modal) -->
    <div class="modal fade action-sheet" id="targetModel" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">ടാർഗറ്റ് സെറ്റ് ചെയ്യുക</h5>
                </div>
                <div class="modal-body">
                    <div class="action-sheet-content">
                        <div class="form-group basic">
                            <label class="label">{{ targetModal.name }}</label>
                        </div>
                        <!-- Parent target & allocated info -->
                        <div style="background:#f4f7ff;border-radius:12px;padding:12px 14px;margin-bottom:16px;">
                            <div style="font-size:11px;font-weight:700;color:#94a3b8;letter-spacing:0.5px;margin-bottom:8px;text-transform:uppercase;">ടാർഗറ്റ് വിവരങ്ങൾ</div>
                            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
                                <span style="font-size:13px;color:#64748b;">ആകെ ടാർഗറ്റ്</span>
                                <span style="font-size:15px;font-weight:800;color:#1a418c;">₹{{ count_target.toLocaleString('en-IN') }}</span>
                            </div>
                            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
                                <span style="font-size:13px;color:#64748b;">മറ്റുള്ളവർക്ക് നൽകിയത്</span>
                                <span style="font-size:15px;font-weight:700;color:#e67e22;">₹{{ targetModalOthersAllocated.toLocaleString('en-IN') }}</span>
                            </div>
                            <div style="display:flex;justify-content:space-between;align-items:center;border-top:1px solid #dde3f0;padding-top:8px;">
                                <span style="font-size:13px;color:#64748b;">ബാക്കി ലഭ്യം</span>
                                <span :style="{fontSize:'15px',fontWeight:'800',color: targetModalRemaining >= 0 ? '#1DCC70' : '#e74c3c'}">
                                    ₹{{ targetModalRemaining.toLocaleString('en-IN') }}
                                </span>
                            </div>
                        </div>

                        <div class="form-group basic">
                            <label class="label">ടാർഗറ്റ് തുക (₹)</label>
                            <div class="form-group boxed">
                                <input v-model.number="targetModal.input_value" type="number" min="0" step="1000"
                                    class="form-control" style="text-align:center;font-weight:700;font-size:16px;"
                                    placeholder="0" />
                            </div>
                        </div>

                        <div class="form-group basic">
                            <button type="button" class="btn btn-primary btn-block btn-lg"
                                :disabled="targetModal.saving" @click="saveTarget">
                                <span v-show="targetModal.saving" class="spinner-border spinner-border-sm"></span>
                                <span>സേവ് ചെയ്യുക</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Form, Field } from "vee-validate";
import UserService from "../../services/user.service";
import $ from 'jquery';

import AlertModel from "./../layouts/AlertModel.vue";
import DialogModel from "./../layouts/DialogModel.vue";
import HelpDeskCard from "../../components/HelpDeskCard.vue";
import TutorialButton from "../../components/TutorialButton.vue";
export default {
    name: 'Nerpatham Report',
    components: {
        Form,
        Field,
        AlertModel,
        DialogModel,
        HelpDeskCard,
        TutorialButton
    },
    props: {
        unit_id: null
    },
    data() {
        return {
            loading: false,
            loading_list: false,
            message: "",
            country_code: '91',
            country_code_whatsapp: '91',
            mobile_number: '',
            whatsapp_number: '',
            wing: '',
            stay: '',
            user_level: "",
            sub_levels: [],
            member_lists: [],
            member_list: [],
            member: [],
            member_edit: true,
            orgnal_member_lists: [],
            trash_list: [],
            unit: [],
            editable_id: '',
            editable: false,
            permissions: [],
            add_admin: false,
            abroad_fields: false,
            pin_number: '',
            attendance: 'A',
            attendance_remarks: '',
            search_qty: '',
            member_id: '',
            alert_type: '',
            alert_message: '',
            pdf_exporting: false,
            dialog_message: '',
            dialog_action: '',
            dialog_action_params: [],
            attendance_present: [],
            attendance_absent: [],
            filter_wing: '',
            isMember: '',
            isSubscriber: '',
            receipt_no: '',
            subscription_no: '',
            isEdit: false,
            count_total: 0,
            count_target: 0,
            level: [],
            targetModal: { show: false, id: null, name: '', entity_level: null, input_value: 0, saving: false },
            nerpatham_target: '',
            target_title: 'ടാർഗറ്റ്',
            page_title: 'വർക്കിങ് ഫണ്ട് റിപ്പോർട്ട്',
            series: [0, 0],
            chartOptions: {
                chart: {
                    width: '100%',
                    type: 'pie',
                },
                colors: ['#64b167', '#db7491'],
                labels: ['Target', 'Total'],
                responsive: [{
                    breakpoint: 100,
                    options: {
                        chart: {
                            width: '100%'
                        },
                        legend: {
                            position: 'bottom'
                        }
                    }
                }]
            },
            count_state:0,
            count_unit:0,
            count_zone:0,
            count_district:0,
            zone_state_share: 0,
            zone_district_share: 0,
            zone_zone_share: 0,
            zone_unit_share: 0,
            district_state_share: 0,
            district_district_share: 0,
            district_zone_share: 0,
            district_unit_share: 0,
            state_state_share: 0,
            state_district_share: 0,
            state_zone_share: 0,
            state_unit_share: 0,
            is_youth_dawa_fund: false,
            pledge_stats: { total: 0, pending: 0, collected: 0, members_count: 0 },
            pledgeBreakdownRows: [],
            pledgeBreakdownLoading: false,
            pledgeBreakdownTitle: '',
            receipt_book_no_input: '',
            receipt_book_unit_id: null,
            receipt_book_unit_name: '',
            // Own receipt books (district/zone level)
            ownReceiptBooks: [],
            // Receipt book allocation
            receiptSummary: {},
            availableBooks: [],
            allocate_sub_id: null,
            allocate_sub_name: '',
            alloc_book_from: '',
            alloc_book_to: '',
            alloc_book_numbers: [],
            alloc_book_number: '',
            alloc_receipt_from: '',
            alloc_receipt_to: '',
            // holder search (zone→unit allocation)
            alloc_holder_search: '',
            alloc_holder_results: [],
            alloc_holder_searching: false,
            alloc_holder_selected: null,
            alloc_holder_timer: null,
            // View / edit / delete books (state admin)
            viewBooksList: [],
            viewBooksLoading: false,
            viewBooks_sub_id: null,
            viewBooks_sub_name: '',
            viewBooksPledgeStats: { total: 0, pending: 0, collected: 0 },
            edit_book_id: null,
            edit_book_number: '',
            edit_receipt_from: '',
            edit_receipt_to: '',
            // edit holder (zone→unit)
            edit_holder_search: '',
            edit_holder_results: [],
            edit_holder_searching: false,
            edit_holder_selected: null,
            edit_holder_timer: null,
            zone_incharges: [],
            zoneInchargesList: [],
            zoneInchargesLoading: false,
            zoneInchargesSingleUnit: false,
            zoneIncharges_district_name: '',
            // edit incharge (state admin)
            editIncharge_ic: null,
            editIncharge_search: '',
            editIncharge_results: [],
            editIncharge_searching: false,
            editIncharge_selected: null,
            editIncharge_saving: false,
            editIncharge_resetting: false,
            editIncharge_msg: '',
            editIncharge_searchTimer: null,
            // book timeline
            timeline_book: null,
            timeline_entries: [],
            timeline_loading: false,
            // visitor suggestions
            visitorList: [],
            visitorLoading: false,
            visitorSaving: false,
            visitorMsg: '',
            visitor_name: '',
            visitor_mobile: '',
            visitor_remarks: '',
            visitor_district_id: null,
            visitor_zone_id: null,
        };
    },

    computed: {
        currentUser() {
            return this.$store.state.auth.user;
        },
        targetModalOthersAllocated() {
            if (!this.targetModal.show) return 0
            return (this.member_lists || [])
                .filter(s => s.id !== this.targetModal.id)
                .reduce((sum, s) => sum + (parseFloat(s.target) || 0), 0)
        },
        targetModalRemaining() {
            return this.count_target - this.targetModalOthersAllocated - (parseFloat(this.targetModal.input_value) || 0)
        },
        thisLevelStatus() {
            return this.user_level === 2 ? 'with_district' : 'with_zone';
        },
        thisLevelReceivedStatus() {
            return this.user_level === 2 ? 'district_received' : 'zone_received';
        },
        thisLevelBooks() {
            // Books held at this level: either confirmed received or still pending at this level
            const pending = this.thisLevelStatus;
            const received = this.thisLevelReceivedStatus;
            return this.ownReceiptBooks.filter(b => b.status === pending || b.status === received);
        },
        allOwnBooksReceived() {
            return this.thisLevelBooks.length > 0 && this.thisLevelBooks.every(b => b.received);
        },
        hasReceivedOwnBook() {
            return this.ownReceiptBooks.some(b => b.received);
        },
        pendingOwnBooksCount() {
            const pendingStatus = this.thisLevelStatus;
            return this.ownReceiptBooks.filter(b => !b.received && b.status === pendingStatus).length;
        },
        ownReceiptBookRange() {
            const nums = this.thisLevelBooks
                .map(b => b.book_number)
                .filter(Boolean)
                .map(Number)
                .sort((a, b) => a - b);
            if (!nums.length) return '—';
            const ranges = [];
            let start = nums[0], end = nums[0];
            for (let i = 1; i < nums.length; i++) {
                if (nums[i] === end + 1) {
                    end = nums[i];
                } else {
                    ranges.push(start === end ? `${start}` : `${start}-${end}`);
                    start = end = nums[i];
                }
            }
            ranges.push(start === end ? `${start}` : `${start}-${end}`);
            return 'ബുക്ക് ' + ranges.join(', ');
        },
        receiptBookPercent() {
            const entries = Object.values(this.receiptSummary);
            if (!entries.length) return null;
            const total    = entries.reduce((s, r) => s + (r.total    || 0), 0);
            const received = entries.reduce((s, r) => s + (r.received || 0), 0);
            if (!total) return null;
            return Math.round((received / total) * 100);
        },
        receiptBookPercentColor() {
            const p = this.receiptBookPercent;
            if (p === null) return '#888';
            if (p === 0)   return '#e74c3c';
            if (p === 100) return '#1DCC70';
            return '#e67e22';
        },
        minusToZero() {
            let value = this.count_target - this.count_total;
            if (value <= 0) {
                return '0';
            } else {
                return value.toFixed(2);
            }
        },
        displayStateShare() {
            if (this.user_level === 3) return this.zone_state_share;
            if (this.user_level === 2) return this.district_state_share;
            if (this.user_level === 1) return this.state_state_share;
            return 0;
        },
        displayDistrictShare() {
            if (this.user_level === 3) return this.zone_district_share;
            if (this.user_level === 2) return this.district_district_share;
            if (this.user_level === 1) return this.state_district_share;
            return 0;
        },
        displayZoneShare() {
            if (this.user_level === 3) return this.zone_zone_share;
            if (this.user_level === 2) return this.district_zone_share;
            if (this.user_level === 1) return this.state_zone_share;
            return 0;
        },
        displayUnitShare() {
            if (this.user_level === 3) return this.zone_unit_share;
            if (this.user_level === 2) return this.district_unit_share;
            if (this.user_level === 1) return this.state_unit_share;
            return 0;
        },
        allocateTitle() {
            if (this.user_level === 1) return 'Allocate Books to District';
            if (this.user_level === 2) return 'Allocate Books to Zone';
            if (this.user_level === 3) return 'Allocate Book to Unit';
            return 'Allocate Books';
        },
    },
    mounted() {
        if (!this.currentUser) {
            location.replace('/');
        } else {
            this.user_level = parseInt(this.$route.params.sub_level);
            // Unit-level (4) should use the UnitLevel component, not HighLevelReport
            if (this.user_level === 4) {
                this.$router.replace(
                    '/org/' + this.$route.params.sub_level + '/' + this.$route.params.id +
                    '/fund-collection/' + this.$route.params.type
                );
                return;
            }
            this.getMembersList();
            this.$root.updateParent('വിസ്‌ഡം യൂത്ത് ദഅ്‌വ ഫണ്ട്');
        }

        window.onpopstate = function () {
            if ($('body').hasClass('modal-open')) {
                location.reload();
            }
        };
    },
    methods: {
        formatAmt(v) {
            const n = parseFloat(v || 0);
            return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        updateChart() {
            this.chartOptions = {
                chart: {
                    type: 'pie'
                },
                colors: ['#64b167', '#db7491'],
                labels: ['ചേർത്തത്', 'ബാക്കി ഉള്ളത്'],
                responsive: [{
                    breakpoint: this.count_target,
                    options: {
                        chart: {
                            width: '100%'
                        },
                        legend: {
                            show: false,
                        }
                    }
                }]
            }
            // In the same way, update the series option
            this.series = [parseFloat(this.count_total),parseFloat(this.count_target - this.count_total)]
        },
        adminView(admin) {
            $('#addAdminModel').modal('show');
            this.message = '';
            this.isEdit = true;
            if (admin.member_id) {
                this.member_id = admin.member_id;
                this.disable_member_form = true;
            } else {
                this.disable_member_form = false;
            }
            this.name = admin.name;
            this.mobile = admin.mobile;
            this.subscription_no = admin.subscription_no;
            this.receipt_no = admin.reciept_no;
            this.member_edit = false;
            this.member_list.push(this.member);
            this.designation = admin.designation;
            this.editable_id = admin.id;
            this.add_admin = false;
            this.edit_admin = true;
        },
        adminAdd() {
            $('#addAdminModel').modal('show');
            this.disable_member_form = false;
            this.message = '';
            this.member_id = '';
            this.name = '';
            this.mobile = '';
            this.subscription_no = '';
            this.receipt_no = '';
            this.member_edit = true;
            this.member = '';
            this.editable_id = null;
            this.add_admin = true;
            this.edit_admin = false;
        },
        closeAddAdminModel() {
            $('#addAdminModel').modal('hide');
        },
        addAdmin(user) {
            console.log(user);
            user.editable_id = this.editable_id;
            user.is_member = this.isMember;
            if (!this.member && this.isMember == 'Y') {
                this.message = 'ദയവായി മെമ്പറെ ലിസ്റ്റിൽ നിന്നും സെലക്ട് ചെയ്തതിനു ശേഷം Save അമർത്തുക';
            }
            if (this.member) {
                user.member_id = this.member.id;
            }
            user.unit_id = parseInt(this.$route.params.id);
            UserService.addAdmin('store-nerpatham-subscriptions', user).then(
                (response) => {
                    if (response.data.status == 'success') {
                        $('#addAdminModel').modal('hide')
                        this.alert_type = 'Success';
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                        this.getMembersList();
                        this.loading = false;
                        this.message = '';
                    } else if (response.data.status == 'warning') {
                        this.alert_type = 'Warning';
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                        this.loading = false;
                    } else if (response.data.status == 'error') {
                        this.alert_type = 'Error';
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                        this.loading = false;
                    } else {
                        this.loading = false;
                    }
                },
                (error) => {
                    this.loading = false;

                    this.messafe = (error.response &&
                        error.response.data &&
                        error.response.data.message) ||
                        error.message ||
                        error.toString();
                }
            );
        },
        adminDeleteConfirmation(admin_list) {
            var data = {
                id: admin_list.id,
            }
            this.dialog_action_params = data;
            this.dialog_message = 'ശരിക്കും ഡിലീറ്റ് ചെയ്യണോ?';
            this.dialog_action = 'adminDelete';
            this.$refs.dialog_model.openModel();
            this.loading = false;
        },
        adminDelete(data) {
            UserService.authPostRequest('delate-nerpatham-subscriptions', data).then(
                (response) => {
                    if (response.data.status == 'success') {
                        this.alert_type = 'Success';
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                        this.getMembersList();
                        this.loading = false;
                    } else if (response.data.status == 'warning') {
                        this.alert_type = 'Warning';
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                        this.loading = false;
                    } else {
                        this.alert_type = 'Error';
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                        this.loading = true;
                    }
                },
                (error) => {
                    this.loading = false;
                    this.message =
                        (error.response &&
                            error.response.data &&
                            error.response.data.message) ||
                        error.message ||
                        error.toString();
                }
            );
        },
        getWing(wing) {
            if (wing == 1)
                return 'വിസ്‌ഡം';
            else if (wing == 2)
                return 'യൂത്ത്';
            else if (wing == 3)
                return 'സ്റ്റുഡന്റസ്';
            else if (wing == 4)
                return 'വിമെൻ';
            else
                return 'ഗേൾസ്';
        },
        getMembersList() {
            this.loading_list = true;
            let data = {
                user_level: this.user_level,
                user_level_id: parseInt(this.$route.params.id),
                type: parseInt(this.$route.params.type)
            }
            UserService.authPostRequest(`get-collection-report`, data).then(
                (response) => {
                    if (response.data.status == 'success') {
                        this.member_lists = response.data.data.list || [];
                        this.level = response.data.data.level;
                        this.count_target = response.data.data.count_target;
                        this.count_total = response.data.data.count_total;
                        this.count_state = response.data.data.count_state;
                        this.count_zone = response.data.data.count_zone;
                        this.count_unit = response.data.data.count_unit;
                        this.count_district = response.data.data.count_district;
                        this.zone_state_share = response.data.data.zone_state_share ?? 0;
                        this.zone_district_share = response.data.data.zone_district_share ?? 0;
                        this.zone_zone_share = response.data.data.zone_zone_share ?? 0;
                        this.zone_unit_share = response.data.data.zone_unit_share ?? 0;
                        this.district_state_share = response.data.data.district_state_share ?? 0;
                        this.district_district_share = response.data.data.district_district_share ?? 0;
                        this.district_zone_share = response.data.data.district_zone_share ?? 0;
                        this.district_unit_share = response.data.data.district_unit_share ?? 0;
                        this.state_state_share = response.data.data.state_state_share ?? 0;
                        this.state_district_share = response.data.data.state_district_share ?? 0;
                        this.state_zone_share = response.data.data.state_zone_share ?? 0;
                        this.state_unit_share = response.data.data.state_unit_share ?? 0;
                        this.level = response.data.data.level;
                        this.target_title = response.data.data.target_title;
                        this.page_title = response.data.data.page_title;
                        this.is_youth_dawa_fund = response.data.data.is_youth_dawa_fund ?? false;
                        this.pledge_stats = response.data.data.pledge_stats || { total: 0, pending: 0, collected: 0 };
                        this.loading_list = false;
                        this.updateChart();
                        this.loadReceiptReport();
                        this.loadAvailableBooks();
                        this.loadOwnReceiptBooks();
                        if (this.user_level === 2 && this.is_youth_dawa_fund) {
                            this.loadZoneIncharges();
                        }
                    } else {
                        this.loading_list = true;
                    }
                },
                (error) => {
                    this.loading = false;
                    this.message =
                        (error.response &&
                            error.response.data &&
                            error.response.data.message) ||
                        error.message ||
                        error.toString();
                }
            );
        },
        
        showActionModel() {
            this.pin_number = '';
        },
        nerpathamTarget(sub_level = null) {
            this.nerpatham_target = sub_level.nerpatham_target;
            this.sub_level_id = sub_level.id;
            $('#nerpathamTargetModel').modal('show');
        },
        openReceiptBookModal(sub_level) {
            this.receipt_book_unit_id = sub_level.id;
            this.receipt_book_unit_name = sub_level.name;
            this.receipt_book_no_input = sub_level.receipt_book_no || '';
            this.message = '';
            $('#receiptBookModel').modal('show');
        },
        saveReceiptBookNo() {
            this.loading = true;
            this.message = '';
            UserService.authPostRequest('set-fund-collection-receipt-book', {
                unit_id: this.receipt_book_unit_id,
                fund_type: this.$route.params.type,
                receipt_book_no: this.receipt_book_no_input,
            }).then(
                (response) => {
                    this.loading = false;
                    if (response.data.status === 'success') {
                        $('#receiptBookModel').modal('hide');
                        this.alert_type = 'Success';
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                        this.getMembersList();
                    } else {
                        this.message = response.data.message;
                    }
                },
                (error) => {
                    this.loading = false;
                    this.message = error.message || error.toString();
                }
            );
        },
        loadReceiptReport() {
            if (this.user_level > 3 || !this.is_youth_dawa_fund) return;
            UserService.authPostRequest('receipt-book/received-report', {
                fund_type: this.$route.params.type,
                level: this.user_level,
                id: parseInt(this.$route.params.id),
            }).then((response) => {
                if (response.data.status === 'success') {
                    const map = {};
                    (response.data.data.rows || []).forEach((r) => {
                        map[r.id] = r;
                    });
                    this.receiptSummary = map;
                }
            });
        },
        loadOwnReceiptBooks() {
            if (!this.is_youth_dawa_fund || (this.user_level !== 2 && this.user_level !== 3)) {
                this.ownReceiptBooks = [];
                return;
            }
            UserService.authPostRequest('receipt-book/list', {
                fund_type: this.$route.params.type,
                level: this.user_level,
                id: parseInt(this.$route.params.id),
            }).then((response) => {
                if (response.data.status === 'success') {
                    this.ownReceiptBooks = response.data.data.books || [];
                }
            });
        },
        markAllOwnBooksReceivedConfirmation() {
            // Refresh book list first so newly-allocated books are included
            UserService.authPostRequest('receipt-book/list', {
                fund_type: this.$route.params.type,
                level: this.user_level,
                id: parseInt(this.$route.params.id),
            }).then((response) => {
                if (response.data.status === 'success') {
                    this.ownReceiptBooks = response.data.data.books || [];
                }
                const pendingStatus = this.thisLevelStatus;
                const unreceivedBooks = this.ownReceiptBooks
                    .filter(b => !b.received && b.status === pendingStatus);
                if (unreceivedBooks.length === 0) {
                    this.alert_type = 'Info';
                    this.alert_message = 'എല്ലാ ബുക്കുകളും ഇതിനകം Received ആയി മാർക്ക് ചെയ്തിട്ടുണ്ട്.';
                    this.$refs.alert_model.openModel();
                    return;
                }
                const pendingBookNos = unreceivedBooks.map(b => '<b>Book ' + b.book_number + '</b>').join(', ');
                this.dialog_message = 'ഇനിപ്പറയുന്ന ബുക്കുകൾ received ആയി മാർക്ക് ചെയ്യണോ?<br><span style="font-size:13px;color:#333;">' + pendingBookNos + '</span>';
                this.dialog_action = 'confirmAllOwnMarkReceived';
                this.dialog_action_params = { ids: unreceivedBooks.map(b => b.id) };
                this.$refs.dialog_model.openModel();
            });
        },
        confirmAllOwnMarkReceived(data) {
            const ids = data.ids || [];
            if (!ids.length) return;
            UserService.authPostRequest('receipt-book/mark-all-received', { ids }).then((response) => {
                this.alert_type = 'Success';
                this.alert_message = response.data.message || 'Receipt books marked as received.';
                this.$refs.alert_model.openModel();
                this.loadOwnReceiptBooks();
                this.loadReceiptReport();
            }).catch(() => {
                this.alert_type = 'Error';
                this.alert_message = 'Failed to mark books as received.';
                this.$refs.alert_model.openModel();
            });
        },
        exportZoneAllocationPdf() {
            const zoneId = parseInt(this.$route.params.id);
            const fundType = this.$route.params.type;
            const year = new Date().getFullYear();
            UserService.authGetRequest('receipt-book/zone-allocation-pdf', {
                zone_id: zoneId,
                fund_type: fundType,
                year: year,
            }, { responseType: 'blob' }).then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `receipt-book-zone-${zoneId}-${year}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            }).catch(() => {
                this.alert_type = 'Error';
                this.alert_message = 'Failed to generate PDF.';
                this.$refs.alert_model.openModel();
            });
        },
        exportDistrictProgressPdf(district) {
            if (this.pdf_exporting) return;
            this.pdf_exporting = true;
            const districtId = district.id;
            const fundType = this.$route.params.type;
            const year = new Date().getFullYear();
            UserService.authGetRequest('fund-collection/district-progress-pdf', {
                district_id: districtId,
                type: fundType,
                year: year,
            }, { responseType: 'blob' }).then((response) => {
                this.pdf_exporting = false;
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `youth-dawa-fund-district-progress-${districtId}-${year}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            }).catch(() => {
                this.pdf_exporting = false;
                this.alert_type = 'Error';
                this.alert_message = 'Failed to generate PDF.';
                this.$refs.alert_model.openModel();
            });
        },
        // Header "Download Report" button — state admin gets every district, district admin
        // gets their own district's zones (exportDistrictProgressPdf, shared with the
        // per-district card button on the state-level list).
        exportProgressReport() {
            if (this.user_level === 1) {
                this.exportStatewideProgressPdf();
            } else if (this.user_level === 2) {
                this.exportDistrictProgressPdf({ id: parseInt(this.$route.params.id) });
            }
        },
        exportStatewideProgressPdf() {
            if (this.pdf_exporting) return;
            this.pdf_exporting = true;
            const fundType = this.$route.params.type;
            const year = new Date().getFullYear();
            UserService.authGetRequest('fund-collection/statewide-progress-pdf', {
                type: fundType,
                year: year,
            }, { responseType: 'blob' }).then((response) => {
                this.pdf_exporting = false;
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `youth-dawa-fund-statewide-progress-${year}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            }).catch(() => {
                this.pdf_exporting = false;
                this.alert_type = 'Error';
                this.alert_message = 'Failed to generate PDF.';
                this.$refs.alert_model.openModel();
            });
        },
        loadAvailableBooks() {
            if (!this.is_youth_dawa_fund || (this.user_level !== 1 && this.user_level !== 2 && this.user_level !== 3)) {
                this.availableBooks = [];
                return;
            }
            UserService.authPostRequest('receipt-book/available-books', {
                fund_type: this.$route.params.type,
                level: this.user_level,
                id: parseInt(this.$route.params.id),
            }).then((response) => {
                if (response.data.status === 'success') {
                    this.availableBooks = response.data.data.book_numbers || [];
                }
            });
        },
        searchAllocHolder() {
            clearTimeout(this.alloc_holder_timer);
            if (!this.alloc_holder_search || this.alloc_holder_search.length < 2) {
                this.alloc_holder_results = [];
                return;
            }
            this.alloc_holder_searching = true;
            this.alloc_holder_timer = setTimeout(() => {
                UserService.authGetRequest('members/search', {
                    search: this.alloc_holder_search,
                    zone_id: parseInt(this.$route.params.id),
                }).then((response) => {
                    this.alloc_holder_searching = false;
                    this.alloc_holder_results = response.data.data?.list || [];
                }).catch(() => { this.alloc_holder_searching = false; });
            }, 400);
        },
        searchEditHolder() {
            clearTimeout(this.edit_holder_timer);
            if (!this.edit_holder_search || this.edit_holder_search.length < 2) {
                this.edit_holder_results = [];
                return;
            }
            this.edit_holder_searching = true;
            this.edit_holder_timer = setTimeout(() => {
                UserService.authGetRequest('members/search', {
                    search: this.edit_holder_search,
                    zone_id: parseInt(this.$route.params.id),
                }).then((response) => {
                    this.edit_holder_searching = false;
                    this.edit_holder_results = response.data.data?.list || [];
                }).catch(() => { this.edit_holder_searching = false; });
            }, 400);
        },
        openTargetModal(sub_level) {
            // Target button only shows on units (view level 3) → entity is the unit (level 4)
            const entityLevel = this.user_level + 1
            this.targetModal = {
                show: true,
                id: sub_level.id,
                name: sub_level.name,
                entity_level: entityLevel,
                input_value: parseFloat(sub_level.target) || 0,
                saving: false,
            }
            $('#targetModel').modal('show')
        },
        saveTarget() {
            if (this.targetModal.saving) return
            this.targetModal.saving = true
            UserService.authPostRequest('set-manual-fund-collection-target', {
                type:         parseInt(this.$route.params.type),
                entity_level: this.targetModal.entity_level,
                entity_id:    this.targetModal.id,
                target:       this.targetModal.input_value,
            }).then(response => {
                this.targetModal.saving = false
                if (response.data.status === 'success') {
                    // Update target in the list immediately
                    const row = (this.member_lists || []).find(s => s.id === this.targetModal.id)
                    if (row) row.target = parseFloat(this.targetModal.input_value) || 0
                    this.targetModal.show = false
                    $('#targetModel').modal('hide')
                } else {
                    alert(response.data.message || 'Error saving target.')
                }
            }).catch(() => { this.targetModal.saving = false; alert('Network error.') })
        },
        openAllocateModal(sub_level) {
            this.allocate_sub_id = sub_level.id;
            this.allocate_sub_name = sub_level.name;
            this.message = '';
            this.alloc_book_from = '';
            this.alloc_book_to = '';
            this.alloc_book_numbers = [];
            this.alloc_book_number = '';
            this.alloc_receipt_from = '';
            this.alloc_receipt_to = '';
            this.alloc_holder_search = '';
            this.alloc_holder_results = [];
            this.alloc_holder_selected = null;
            $('#allocateBookModel').modal('show');
        },
        submitAllocate() {
            this.loading = true;
            this.message = '';
            let endpoint = '';
            let data = { fund_type: this.$route.params.type };
            if (this.user_level === 1) {
                if (!this.alloc_book_from || !this.alloc_book_to) {
                    this.loading = false;
                    this.message = 'Select Book Number From and To.';
                    return;
                }
                endpoint = 'receipt-book/allocate-to-district';
                data.district_id = this.allocate_sub_id;
                data.book_from = this.alloc_book_from;
                data.book_to = this.alloc_book_to;
            } else if (this.user_level === 2) {
                if (!this.alloc_book_numbers.length) {
                    this.loading = false;
                    this.message = 'Select book numbers to allocate.';
                    return;
                }
                endpoint = 'receipt-book/allocate-to-zone';
                data.zone_id = this.allocate_sub_id;
                data.book_numbers = this.alloc_book_numbers;
            } else if (this.user_level === 3) {
                if (!this.alloc_book_numbers.length) {
                    this.loading = false;
                    this.message = 'Select book numbers to allocate.';
                    return;
                }
                endpoint = 'receipt-book/allocate-to-unit';
                data.unit_id = this.allocate_sub_id;
                data.book_numbers = this.alloc_book_numbers;
                if (this.alloc_holder_selected) {
                    data.holder_member_id = this.alloc_holder_selected.id;
                    data.holder_name = this.alloc_holder_selected.name;
                }
            } else {
                this.loading = false;
                return;
            }
            UserService.authPostRequest(endpoint, data).then(
                (response) => {
                    this.loading = false;
                    if (response.data.status === 'success') {
                        $('#allocateBookModel').modal('hide');
                        this.alert_type = 'Success';
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                        this.loadReceiptReport();
                        this.loadAvailableBooks();
                    } else {
                        this.message = response.data.message;
                    }
                },
                (error) => {
                    this.loading = false;
                    this.message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
                }
            );
        },
        openViewBooksModal(sub_level) {
            this.viewBooks_sub_id = sub_level.id;
            this.viewBooks_sub_name = sub_level.name;
            this.viewBooksList = [];
            this.viewBooksLoading = true;
            $('#viewBooksModel').modal('show');
            // user_level is the current viewer; sub_level is one level below
            // level param for getBooks = user_level + 1 (child entity level)
            const childLevel = this.user_level + 1;
            UserService.authPostRequest('receipt-book/list', {
                fund_type: this.$route.params.type,
                level: childLevel,
                id: sub_level.id,
            }).then((response) => {
                this.viewBooksLoading = false;
                if (response.data.status === 'success') {
                    const books = response.data.data.books || [];
                    // Not-received first, received at bottom
                    books.sort((a, b) => (a.received ? 1 : 0) - (b.received ? 1 : 0));
                    this.viewBooksList = books;
                    this.viewBooksPledgeStats = response.data.data.pledge_stats || { total: 0, pending: 0, collected: 0 };
                }
            }).catch(() => { this.viewBooksLoading = false; });
        },
        openEditBookModal(book) {
            this.edit_book_id = book.id;
            this.edit_book_number = book.book_number;
            this.edit_receipt_from = book.receipt_from || '';
            this.edit_receipt_to = book.receipt_to || '';
            this.edit_holder_search = '';
            this.edit_holder_results = [];
            this.edit_holder_selected = book.holder_name
                ? { id: book.holder_member_id || null, name: book.holder_name, mobile: '' }
                : null;
            this.message = '';
            // For district/zone level view: load available books and include current book in list
            const viewLevel = parseInt(this.$route.params.sub_level);
            if (viewLevel >= 2 && viewLevel <= 3) {
                UserService.authPostRequest('receipt-book/available-books', {
                    fund_type: this.$route.params.type,
                    level: viewLevel,
                    id: parseInt(this.$route.params.id),
                }).then((response) => {
                    if (response.data.status === 'success') {
                        const books = response.data.data.book_numbers || [];
                        // Add current book at top so it shows as pre-selected
                        if (!books.includes(book.book_number)) {
                            books.unshift(book.book_number);
                        }
                        this.availableBooks = books;
                    }
                });
            }
            $('#editBookModel').modal('show');
        },
        submitEditBook() {
            this.loading = true;
            this.message = '';
            // Use reassign when viewing district/zone level (radio list is shown)
            const viewLevel  = parseInt(this.$route.params.sub_level);
            const isReassign = viewLevel >= 2 && viewLevel <= 3;
            const endpoint   = isReassign ? 'receipt-book/reassign' : 'receipt-book/update';
            const holderData = this.user_level === 3
                ? { holder_member_id: this.edit_holder_selected?.id || null, holder_name: this.edit_holder_selected?.name || null }
                : {};
            const payload    = isReassign
                ? { old_id: this.edit_book_id, new_book_number: this.edit_book_number, ...holderData }
                : { id: this.edit_book_id, book_number: this.edit_book_number, receipt_from: this.edit_receipt_from, receipt_to: this.edit_receipt_to, ...holderData };

            UserService.authPostRequest(endpoint, payload).then((response) => {
                this.loading = false;
                if (response.data.status === 'success') {
                    $('#editBookModel').modal('hide');
                    this.alert_type = 'Success';
                    this.alert_message = response.data.message;
                    this.$refs.alert_model.openModel();
                    this.openViewBooksModal({ id: this.viewBooks_sub_id, name: this.viewBooks_sub_name });
                    this.loadReceiptReport();
                } else {
                    this.message = response.data.message;
                }
            }).catch((error) => {
                this.loading = false;
                this.message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            });
        },
        deleteBookConfirmation(book) {
            if (book.zone_id && this.user_level === 2) {
                this.dialog_message = 'Book #' + book.book_number + ' മണ്ഡലത്തിൽ നിന്ന് തിരിച്ചുവിളിക്കണോ? ഇത് ചെയ്താൽ ബുക്ക് വീണ്ടും allot ചെയ്യാൻ കഴിയും.';
            } else if (book.unit_id && this.user_level === 3) {
                this.dialog_message = 'Book #' + book.book_number + ' ശാഖയിൽ നിന്ന് തിരിച്ചുവിളിക്കണോ? ഇത് ചെയ്താൽ ബുക്ക് വീണ്ടും allot ചെയ്യാൻ കഴിയും.';
            } else {
                this.dialog_message = 'Delete book #' + book.book_number + '? This cannot be undone.';
            }
            this.dialog_action = 'deleteBook';
            this.dialog_action_params = { id: book.id };
            this.$refs.dialog_model.openModel();
        },
        deleteBook(data) {
            UserService.authPostRequest('receipt-book/delete', data).then((response) => {
                if (response.data.status === 'success') {
                    this.alert_type = 'Success';
                    this.alert_message = response.data.message;
                    this.$refs.alert_model.openModel();
                    this.openViewBooksModal({ id: this.viewBooks_sub_id, name: this.viewBooks_sub_name });
                    this.loadReceiptReport();
                } else {
                    this.alert_type = 'Warning';
                    this.alert_message = response.data.message;
                    this.$refs.alert_model.openModel();
                }
            });
        },
        markBookReceived(book) {
            this.dialog_message = 'Book #' + book.book_number + ' received ആയി മാർക്ക് ചെയ്യണോ?';
            this.dialog_action = 'confirmMarkReceived';
            this.dialog_action_params = { id: book.id };
            this.$refs.dialog_model.openModel();
        },
        confirmMarkReceived(data) {
            UserService.authPostRequest('receipt-book/mark-received', data).then((response) => {
                if (response.data.status === 'success') {
                    this.alert_type = 'Success';
                    this.alert_message = response.data.message;
                    this.$refs.alert_model.openModel();
                    this.openViewBooksModal({ id: this.viewBooks_sub_id, name: this.viewBooks_sub_name });
                    this.loadReceiptReport();
                } else {
                    this.alert_type = 'Warning';
                    this.alert_message = response.data.message;
                    this.$refs.alert_model.openModel();
                }
            });
        },
        openPledgeBreakdownModal() {
            const titles = { 1: 'ജില്ല തിരിച്ച്', 2: 'മണ്ഡലം തിരിച്ച്', 3: 'ശാഖ തിരിച്ച്' };
            this.pledgeBreakdownTitle = titles[this.user_level] || '';
            this.pledgeBreakdownRows = [];
            this.pledgeBreakdownLoading = true;
            $('#pledgeBreakdownModel').modal('show');
            UserService.authPostRequest('get-pledge-stats-breakdown', {
                type: this.$route.params.type,
                user_level: this.user_level,
                user_level_id: parseInt(this.$route.params.id),
            }).then((response) => {
                this.pledgeBreakdownLoading = false;
                if (response.data.status === 'success') {
                    this.pledgeBreakdownRows = response.data.data.rows || [];
                }
            }).catch(() => { this.pledgeBreakdownLoading = false; });
        },
        openZoneInchargesModal(sub_level) {
            // state admin (1) passes district, district admin (2) passes zone, zone admin (3) passes unit
            const isDistrictAdmin = this.user_level === 2;
            const isZoneAdmin = this.user_level === 3;
            this.zoneIncharges_district_name = sub_level ? sub_level.name : '';
            this.zoneInchargesList = [];
            this.zoneInchargesLoading = true;
            $('#zoneInchargesModel').modal('show');
            const payload = { type: parseInt(this.$route.params.type) };
            let unitFilter = null;
            if (isZoneAdmin) {
                // zone admin clicked a unit → show that unit's incharge
                payload.zone_id = parseInt(this.$route.params.id);
                unitFilter = sub_level ? parseInt(sub_level.id) : null;
            } else if (isDistrictAdmin) {
                payload.district_id = parseInt(this.$route.params.id);
                payload.zone_id = sub_level ? parseInt(sub_level.id) : null;
            } else {
                payload.district_id = sub_level ? parseInt(sub_level.id) : parseInt(this.$route.params.id);
            }
            this.zoneInchargesSingleUnit = !!unitFilter;
            UserService.authPostRequest('get-zone-incharges', payload).then((response) => {
                this.zoneInchargesLoading = false;
                if (response.data.status === 'success') {
                    let list = response.data.data.list || [];
                    if (unitFilter) {
                        // Zone admin drilled into a unit → that unit's Zone Incharge field
                        // followed by the unit's real Treasurer record.
                        list = list.filter(ic =>
                            (ic.is_incharge_field && ic.user_level === 3 && ic.unit_id === unitFilter) ||
                            (!ic.is_incharge_field && ic.user_level === 4 && ic.unit_id === unitFilter)
                        );
                        list = [...list].sort((a, b) => a.user_level - b.user_level);
                    } else {
                        // District admin listing a zone → District Incharge field + the real
                        // treasurer listing (zone treasurer + each unit's treasurer). Exclude
                        // only the per-unit Zone Incharge FIELDS — real zone treasurers stay,
                        // even when their exec record happens to carry a unit_id.
                        list = list.filter(ic => !(ic.is_incharge_field && ic.user_level === 3));
                    }
                    this.zoneInchargesList = list;
                }
            }).catch(() => { this.zoneInchargesLoading = false; });
        },
        openEditIncharge(ic) {
            this.editIncharge_ic = ic;
            this.editIncharge_search = '';
            this.editIncharge_results = [];
            this.editIncharge_selected = null;
            this.editIncharge_msg = '';
            this.editIncharge_resetting = false;
            $('#editInchargeModel').modal('show');
        },
        openBookTimeline(book) {
            this.timeline_book = book;
            this.timeline_entries = [];
            this.timeline_loading = true;
            $('#bookTimelineModal').modal('show');
            UserService.authPostRequest('receipt-book/timeline', {
                id: book.id,
                fund_type: this.$route.params.type,
                year: this.listYear,
            }).then((response) => {
                this.timeline_loading = false;
                if (response.data.status === 'success') {
                    this.timeline_entries = response.data.data.timeline || [];
                    if (response.data.data.book) {
                        this.timeline_book = { ...this.timeline_book, ...response.data.data.book };
                    }
                }
            }).catch(() => { this.timeline_loading = false; });
        },
        searchInchargeMember() {
            clearTimeout(this.editIncharge_searchTimer);
            if (!this.editIncharge_search || this.editIncharge_search.length < 2) {
                this.editIncharge_results = [];
                return;
            }
            this.editIncharge_searching = true;
            this.editIncharge_searchTimer = setTimeout(() => {
                const ic  = this.editIncharge_ic
                const lvl = ic ? parseInt(ic.user_level) : 0
                const params = { q: this.editIncharge_search }
                if (ic) {
                    if (lvl === 4 && ic.zone_id) {
                        // Unit Incharge must be a SECRETARIAT member (any wing) of the unit's zone
                        params.secretariat_level = 3
                        params.zone_id = ic.zone_id
                    } else if (lvl === 2 && ic.district_id) {
                        // District Incharge must be a SECRETARIAT member (any wing) of the district
                        params.secretariat_level = 2
                        params.district_id = ic.district_id
                    } else if (lvl === 3 && ic.zone_id) {
                        // Zone Incharge (zone-wide) must be a SECRETARIAT member (any wing) of that zone
                        params.secretariat_level = 3
                        params.zone_id = ic.zone_id
                    } else if (ic.district_id) {
                        params.district_id = ic.district_id
                    }
                }
                UserService.authGetRequest('members/search', params).then((response) => {
                    this.editIncharge_searching = false;
                    this.editIncharge_results = response.data.data?.list || response.data.data || [];
                }).catch(() => { this.editIncharge_searching = false; });
            }, 400);
        },
        selectInchargeMember(m) {
            const ic  = this.editIncharge_ic
            const lvl = ic ? parseInt(ic.user_level) : 0
            // Membership scope check
            // Unit Incharge is picked from the unit's ZONE secretariat, so the member may
            // belong to a different unit within that same zone — check zone, not unit.
            if (lvl === 4 && ic.zone_id && m.zone_id !== ic.zone_id) {
                this.editIncharge_msg = 'ഈ മെമ്പർ ആ മണ്ഡലത്തിൽ നിന്നല്ല'
                return
            }
            if (lvl === 3 && ic.zone_id && m.zone_id !== ic.zone_id) {
                this.editIncharge_msg = 'ഈ മെമ്പർ ആ മണ്ഡലത്തിൽ നിന്നല്ല'
                return
            }
            if (lvl === 2 && ic.district_id && m.district_id !== ic.district_id) {
                this.editIncharge_msg = 'ഈ മെമ്പർ ആ ജില്ലയിൽ നിന്നല്ല'
                return
            }
            this.editIncharge_msg = ''
            this.editIncharge_selected = m;
            this.editIncharge_results = [];
            this.editIncharge_search = m.name;
        },
        saveInchargeOverride() {
            if (!this.editIncharge_selected || !this.editIncharge_ic) return;
            this.editIncharge_saving = true;
            this.editIncharge_msg = '';
            // Key the override at the exec's OWN level — not the treasurer's home unit/zone.
            const lvl = parseInt(this.editIncharge_ic.user_level);
            UserService.authPostRequest('set-zone-incharge', {
                user_level:  lvl,
                district_id: this.editIncharge_ic.district_id,
                // District incharge (2) is per-zone; Zone/Unit incharge (3/4) are per-unit.
                zone_id:     (lvl === 2 || lvl === 3 || lvl === 4) ? this.editIncharge_ic.zone_id : null,
                unit_id:     (lvl === 3 || lvl === 4) ? this.editIncharge_ic.unit_id : null,
                member_id:   this.editIncharge_selected.id,
                type:        parseInt(this.$route.params.type),
            }).then((response) => {
                this.editIncharge_saving = false;
                if (response.data.status === 'success') {
                    $('#editInchargeModel').modal('hide');
                    // Refresh the incharge list
                    const idx = this.zoneInchargesList.findIndex(i => i.exec_id === this.editIncharge_ic.exec_id)
                    if (idx !== -1) {
                        const updated = [...this.zoneInchargesList]
                        updated[idx] = {
                            ...updated[idx],
                            name: this.editIncharge_selected.name,
                            mobile: this.editIncharge_selected.mobile,
                            is_override: true,
                        }
                        this.zoneInchargesList = updated
                    }
                } else {
                    this.editIncharge_msg = response.data.message || 'Error';
                }
            }).catch(() => {
                this.editIncharge_saving = false;
                this.editIncharge_msg = 'Error saving';
            });
        },
        resetInchargeOverride() {
            if (!this.editIncharge_ic) return;
            this.editIncharge_resetting = true;
            this.editIncharge_msg = '';
            const lvl = parseInt(this.editIncharge_ic.user_level);
            UserService.authPostRequest('delete-zone-incharge', {
                user_level:  lvl,
                district_id: this.editIncharge_ic.district_id,
                zone_id:     (lvl === 2 || lvl === 3 || lvl === 4) ? this.editIncharge_ic.zone_id : null,
                unit_id:     (lvl === 3 || lvl === 4) ? this.editIncharge_ic.unit_id : null,
                type:        parseInt(this.$route.params.type),
            }).then((response) => {
                this.editIncharge_resetting = false;
                if (response.data.status === 'success') {
                    $('#editInchargeModel').modal('hide');
                    const idx = this.zoneInchargesList.findIndex(i => i.exec_id === this.editIncharge_ic.exec_id)
                    if (idx !== -1) {
                        const updated = [...this.zoneInchargesList]
                        updated[idx] = { ...updated[idx], is_override: false }
                        this.zoneInchargesList = updated
                    }
                    // Reload incharge list to pick up the original exec name from the org
                    this.openZoneInchargesModal(this.editIncharge_ic)
                } else {
                    this.editIncharge_msg = response.data.message || 'Error';
                }
            }).catch(() => {
                this.editIncharge_resetting = false;
                this.editIncharge_msg = 'Error resetting';
            });
        },
        goToKaradReport() {
            this.$router.push({
                name: 'Reports',
                params: {
                    sub_level: this.$route.params.sub_level,
                    id:        this.$route.params.id,
                    type:      this.$route.params.type,
                },
            });
        },
        openVisitorListModal(sub_level) {
            this.visitor_district_id = this.user_level === 1 ? sub_level.id : parseInt(this.$route.params.id);
            this.visitor_zone_id     = this.user_level === 2 ? sub_level.id : null;
            this.visitor_name    = '';
            this.visitor_mobile  = '';
            this.visitor_remarks = '';
            this.visitorMsg = '';
            this.visitorList = [];
            this.visitorLoading = true;
            $('#visitorListModel').modal('show');
            this.fetchVisitorSuggestions();
        },
        fetchVisitorSuggestions() {
            this.visitorLoading = true;
            UserService.authPostRequest('get-visitor-suggestions', {
                type:        parseInt(this.$route.params.type),
                district_id: this.visitor_district_id,
                zone_id:     this.visitor_zone_id,
            }).then((response) => {
                this.visitorLoading = false;
                if (response.data.status === 'success') {
                    this.visitorList = response.data.data.list || [];
                }
            }).catch(() => { this.visitorLoading = false; });
        },
        saveVisitorSuggestion() {
            if (!this.visitor_name && !this.visitor_remarks) {
                this.visitorMsg = 'Name or remarks required.';
                return;
            }
            this.visitorSaving = true;
            this.visitorMsg = '';
            UserService.authPostRequest('store-visitor-suggestion', {
                type:        parseInt(this.$route.params.type),
                district_id: this.visitor_district_id,
                zone_id:     this.visitor_zone_id,
                name:        this.visitor_name,
                mobile:      this.visitor_mobile,
                remarks:     this.visitor_remarks,
            }).then((response) => {
                this.visitorSaving = false;
                if (response.data.status === 'success') {
                    this.visitor_name = '';
                    this.visitor_mobile = '';
                    this.visitor_remarks = '';
                    this.fetchVisitorSuggestions();
                } else {
                    this.visitorMsg = response.data.message;
                }
            }).catch(() => { this.visitorSaving = false; });
        },
        toggleVisitorSuggestion(v) {
            UserService.authPostRequest('toggle-visitor-suggestion', { id: v.id }).then((response) => {
                if (response.data.status === 'success') {
                    v.is_active = response.data.data.is_active;
                }
            });
        },
        deleteVisitorSuggestion(v) {
            UserService.authPostRequest('delete-visitor-suggestion', { id: v.id }).then(() => {
                this.fetchVisitorSuggestions();
            });
        },
        loadZoneIncharges() {
            UserService.authPostRequest('get-zone-incharges', {
                district_id: parseInt(this.$route.params.id),
                type: parseInt(this.$route.params.type),
            }).then((response) => {
                if (response.data.status === 'success') {
                    this.zone_incharges = response.data.data.list || [];
                }
            }).catch(() => {});
        },
        addNerpathamTarget(data) {
            data.level = parseInt(this.$route.params.sub_level) + 1;
            data.level_id = parseInt(this.sub_level_id);
            UserService.addAdmin('set-nerpatham-target', data).then(
                (response) => {
                    if (response.data.status == 'success') {
                        this.getMembersList();
                        $('#nerpathamTargetModel').modal('hide')
                        this.alert_type = 'Success';
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                        this.loading = false;
                        this.message = '';
                    } else if (response.data.status == 'warning') {
                        this.alert_type = 'Warning';
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                        this.loading = false;
                    } else if (response.data.status == 'error') {
                        this.alert_type = 'Error';
                        this.alert_message = response.data.message;
                        this.$refs.alert_model.openModel();
                        this.loading = false;
                    } else {
                        this.loading = false;
                    }
                },
                (error) => {
                    this.loading = false;

                    this.messafe = (error.response &&
                        error.response.data &&
                        error.response.data.message) ||
                        error.message ||
                        error.toString();
                }
            );
        },
    },
};
</script>
<style scoped>
/* ===== Export PDF button ===== */
.export-pdf-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 6px 11px;
    border: none;
    border-radius: 8px;
    background: linear-gradient(135deg, #e23744, #c0182a);
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    box-shadow: 0 3px 9px rgba(226,55,68,.25);
    transition: transform .12s ease, box-shadow .12s ease;
}
.export-pdf-btn ion-icon { font-size: 15px; }
.export-pdf-btn:active {
    transform: translateY(1px);
    box-shadow: 0 2px 6px rgba(226,55,68,.28);
}

/* ===== Help Desk card ===== */
.helpdesk-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: #fff;
    border: 1px solid #eef0f5;
    border-radius: 12px;
    padding: 8px 12px;
    box-shadow: 0 2px 10px rgba(0,0,0,.04);
}
.helpdesk-left {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
}
.helpdesk-icon { font-size: 20px; color: #1a418c; flex-shrink: 0; }
.helpdesk-title { font-size: 13px; font-weight: 800; color: #27173E; white-space: nowrap; }
.helpdesk-actions { display: flex; gap: 7px; }
.helpdesk-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    color: #fff;
    text-decoration: none;
}
.helpdesk-btn ion-icon { font-size: 18px; }
.helpdesk-btn.call { background: #1a418c; }
.helpdesk-btn.wa { background: #25D366; }

/* ===== Fund collection sub-level cards ===== */
.fc-card {
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 2px 8px rgba(26, 65, 140, 0.08);
    border: 1px solid #e8ecf4;
    overflow: hidden;
}
.fc-card-link {
    display: block;
    padding: 14px 14px 12px;
    text-decoration: none;
}
.fc-card-link:active {
    background: #f8f9fd;
}
/* Row 1 — index + name + chevron */
.fc-row1 {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}
.fc-index {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #eef2ff;
    color: #3b5bdb;
    border-radius: 50%;
    font-size: 13px;
    font-weight: 800;
}
.fc-name {
    flex: 1;
    min-width: 0;
    font-size: 16px;
    font-weight: 800;
    line-height: 1.3;
    word-break: break-word;
}
.fc-chevron {
    flex-shrink: 0;
    font-size: 18px;
    color: #c2c8d6;
}
/* Row 2 — amount + books badge */
.fc-row2 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 6px;
}
.fc-amount-block {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.fc-row2-right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}
.fc-amount {
    font-size: 20px;
    font-weight: 800;
    line-height: 1;
}
.fc-target-label {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 600;
}
.fc-books-badge {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 13px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
}
.fc-books-badge ion-icon { font-size: 14px; }
.fc-books-done  { background: #1DCC70; color: #fff; }
.fc-books-pending { background: #fff3cd; color: #856404; border: 1px solid #ffc107; }
.fc-books-empty { background: #eef0f5; color: #aab; }
/* Progress */
.fc-progress { margin-top: 10px; }
.fc-progress-track {
    height: 5px;
    background: #eef0f5;
    border-radius: 5px;
    overflow: hidden;
}
.fc-progress-fill {
    height: 100%;
    border-radius: 5px;
    transition: width 0.4s ease;
}
/* Action footer */
.fc-no-holder-warning {
    padding: 6px 12px;
    font-size: 11.5px;
    font-weight: 600;
    color: #d9534f;
    background: #fdf2f2;
    border-top: 1px solid #eef0f5;
}
.fc-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 12px 12px;
    border-top: 1px solid #eef0f5;
    background: #f8f9fd;
}
.fc-btn {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 10px 8px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 800;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    border: none;
    min-width: 0;
}
.fc-btn-full { width: 100%; flex: unset; }
/* District cards have a 3rd (PDF) button — give it its own full-width row below the other
   two, rather than squeezing all three onto one line. Compound selector so it outweighs
   .fc-btn's own `flex: 1` (flex-basis: 0%) regardless of source order. */
.fc-btn.fc-btn-row-break { flex-basis: 100%; }
.fc-btn ion-icon { font-size: 14px; }
.fc-btn-primary {
    background: #1a418c;
    color: #fff;
    box-shadow: 0 2px 6px rgba(26,65,140,0.25);
}
.fc-btn-primary:active { background: #13306a; }
.fc-btn-outline {
    background: #fff;
    color: #3b5bdb;
    border: 1.5px solid #c7d2fe;
}
.fc-btn-outline:active { background: #eef2ff; }
.fc-btn-target {
    background: #fff8f0;
    color: #e67e22;
    border: 1.5px solid #f0a060;
}
.fc-btn-target:active { background: #fef0e0; }
.fc-target-inline-btn {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 9px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    color: #e67e22;
    background: #fff8f0;
    border: 1.5px solid #f0a060;
    text-decoration: none;
    white-space: nowrap;
}
.fc-target-inline-btn ion-icon { font-size: 12px; }
.fc-target-added-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 9px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    color: #1DCC70;
    background: #eafaf1;
    border: 1.5px solid #a8e6c4;
    white-space: nowrap;
    text-decoration: none;
    cursor: pointer;
}
.fc-target-added-badge ion-icon { font-size: 11px; }

.section-heading {
    padding: 10px 10px 0px 10px;
}

.error-feedback {
    color: red;
}

.receipt-book-cell {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    white-space: nowrap;
}

.receipt-book-cell .badge {
    font-size: 11px;
}

.allocate-link {
    line-height: 0;
}

.stat-box .value {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1em;
    color: #27173E;
}
.listview {
    /* margin-bottom: 20px; */
}

.edit-btn {
    margin-left: 8px;
}

.progress {
    width: 80px;
}

.listview>li footer {
    color: #171617;
}

.min-width-50 {
    min-width: 60% !important;
}

.action-button-list>li {
    min-height: 1px !important;
}

.sl-no {
    font-weight: 900;
    margin-right: 12px;
}

.deleted-list-title {
    font-size: 15px;
}

.deleted-list {
    margin-bottom: 20px;
}

.deleted-info {
    background-color: #f2bbca;
    font-size: 12px;
}

.deleted-info strong {
    font-size: 18px;
    font-weight: 600;
}

.image-listview.text>li:after {
    left: 0px;
    /* height: 1px; */
    background-color: #1a418c;
}

.verification-badge {
    margin-left: 5px;
}

.avatar-section .button {
    color: #fff;
    width: 18px;
    height: 18px;
    border-radius: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    position: absolute;
    right: 10px;
    top: 24px;
    bottom: 0;
}

.avatar-section .verified {
    background: #4dc81c;
}

.avatar-section .not-verified {
    background: #eb3941;
}

.wallet-card .wallet-footer .item .icon-wrapper {
    width: 100%;
    height: 48px;
    display: inline-flex;
    align-items: left;
    justify-content: left;
    border-radius: 10px;
    color: #fff;
    /* font-size: 28px; */
    margin-bottom: 14px;
    padding-left: 10px;
}

.wallet-card {
    padding-bottom: 0px !important;

}

.menu-title {
    color: #fff !important;
    font-size: 12px !important;
    padding-left: 5px !important;
    text-align: left !important;
}
.wallet-card .balance .total {
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1em;
    font-size: 26px;
}
.wallet-card .balance .title {
    color: #27173E;
    font-weight: 500;
    font-size: 16px !important;
    display: block;
    margin-bottom: 8px;
}

.stat-box {
    background: #ffffff;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 9%);
    border-radius: 10px;
    padding: 10px 10px;
}
.wallet-card .balance {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    height: auto;
}
.left{
    margin-left: 0px;
}
.wallet-card {
    background: #ffffff;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 9%);
    border-radius: 10px;
    padding: 0px 10px;
    position: relative;
    z-index: 1;
}
</style>